import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AgmMap } from '@agm/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { from, of, Subscription } from 'rxjs';
import { catchError, finalize, map, mergeMap, timeout } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { OrderService } from 'src/app/_services/dispo/order.service';
import { CustomerBranchService } from 'src/app/_services/dispo/customer-branch.service';
import { GeocodingService } from 'src/app/_services/geocoding.service';
import { AddressImportDialogComponent } from './address-import-dialog.component';
import {
  RollerSoapImportDialogComponent,
  RollerSoapImportDialogResult,
} from './roller-soap-import-dialog.component';
import { DispatchPlannerRollerSoapService, DispatchPlannerSoapError } from './dispatch-planner-roller-soap.service';
import { ParsedAddress } from './address-parser.service';
import { decodeCsvBytes } from './dispatch-planner-csv-encoding';
import { DispatchPlannerEmailDialogComponent } from './dispatch-planner-email-dialog.component';
import { DispatchPlannerPdfService } from './dispatch-planner-pdf.service';
import {
  DispatchArticle,
  DispatchCsvStation,
  DispatchPlannerCsvService,
  KontingentType
} from './dispatch-planner-csv.service';
import {
  DispatchPlannerArticlesDialogComponent,
  DispatchPlannerArticlesDialogData
} from './dispatch-planner-articles-dialog.component';
import {
  DispatchPlannerErrorDialogComponent,
  DispatchPlannerErrorDialogData
} from './dispatch-planner-error-dialog.component';
import {
  DispatchBranchOption,
  DispatchPlannerTeamService,
  DispatchTeamBaseOption
} from './dispatch-planner-team.service';
import { buildBranchMarkerIcon, buildCalendarAppointmentMarkerIcon, buildDeliverySuggestionMarkerIcon, buildDispatchMarkerIcon, getDefaultMarkerIconUrl } from './dispatch-planner-markers';
import { DispatchPlannerAutoDispatchService } from './dispatch-planner-auto-dispatch.service';
import {
  DeliverySuggestionRow,
  DispatchPlannerDeliverySuggestionService,
  resolveDeliverySuggestionCustomerFields
} from './dispatch-planner-delivery-suggestion.service';
import {
  DispatchCalendarAppointment,
  DispatchPlannerCalendarService
} from './dispatch-planner-calendar.service';
import { DispatchMapProjection } from './dispatch-planner-map-projection';
import {
  buildMarkerInfoLabelHtml,
  buildMarkerTooltipText,
  createMarkerInfoOverlay,
  getStopNachname,
  getStopPrimaryArticleLabel,
  getStopServiceTypeFullLabel,
  getStopServiceTypeLabel
} from './dispatch-planner-marker-info';
import {
  formatDispatchTermin,
  getStopOrt,
  getStopPlz,
  parsePlzOrtFromAddress
} from './dispatch-planner-stop-format';
import {
  BRANCH_FREE_RADIUS_KM,
  BRANCH_INNER_RADIUS_KM,
  calculateDistanceSurchargeEur,
  calculateStopBaseKosten,
  calculateStopTotalKosten,
  getStopDistanceKmFromBranch
} from './dispatch-planner-distance-surcharge';
import {
  MapPoint,
  MapRect,
  normalizeRect,
  pointInPolygon,
  pointsToSvgPoints,
  rectMeetsMinimumSize
} from './dispatch-planner-map-selection.utils';
import {
  createDefaultPlannerTeams,
  createPlannerTeam,
  DispatchTeamId,
  EMPTY_TEAM_TOUR,
  getPlannerTeamColor,
  getTeamListId,
  PlannerTeam,
  resolveTeamIdFromListId,
  TeamTourState
} from './dispatch-planner-teams';
import { buildTeamCategoryBreakdown, TeamCategoryCount } from './dispatch-planner-team-metrics';

export type MapSelectionTool = 'pan' | 'rectangle' | 'lasso';
export type DispatchListViewMode = 'detail' | 'compact';
export type DispatchMapTypeId = 'roadmap' | 'satellite' | 'hybrid';

export interface DispatchStop {
  id: string;
  address: string;
  lat: number | null;
  lng: number | null;
  geocoding: boolean;
  geocodingFailed: boolean;
  orderIndex: number;
  filiale?: string;
  kaufvertragsnummer?: string;
  kontingent?: KontingentType;
  kontingentLabel?: string;
  customerName?: string;
  nachname?: string;
  plz?: string;
  ort?: string;
  termin?: string;
  tourBezeichnung?: string;
  tourSchluessel?: string;
  positionCount?: number;
  artikelCount?: number;
  liefermenge?: number;
  lieferkostenEK?: number;
  montagemenge?: number;
  montagekostenEK?: number;
  gewichtKg?: number;
  volumenM3?: number;
  kostenGesamt?: number;
  articles?: DispatchArticle[];
  email?: string;
  mobil?: string;
  telefon?: string;
  source?: 'text' | 'csv' | 'delivery-suggestion';
  auftragsart?: string;
  zahlungsart?: string;
  filialbezeichnung?: string;
  kundennummer?: string;
  rechnungsadresse?: string;
  montageFlag?: string;
  lieferfaehigSeit?: string;
  zusatztext?: string;
  /** Team-Slot, wenn der Stopp in einer Teamliste liegt. */
  teamNumber?: DispatchTeamId;
}

export interface TourRouteLeg {
  distance_text: string;
  duration_text: string;
}

export type PlannerListKey = 'available' | 'delivery-suggestion' | DispatchTeamId;
export type PlannerListPanelKey = 'import' | 'deliverySuggestion' | `team:${number}`;

interface DispatchBranchLocation {
  id: number;
  label: string;
  lat: number | null;
  lng: number | null;
  street?: string;
  postal_code?: string;
  place?: string;
  title?: string;
}

@Component({
  selector: 'app-dispatch-planner',
  templateUrl: './dispatch-planner.component.html',
  styleUrls: ['./dispatch-planner.component.css'],
  host: { class: 'mh3-route-dispatch-planner' }
})
export class DispatchPlannerComponent implements OnInit, OnDestroy {
  @ViewChild(AgmMap) map: AgmMap;
  @ViewChild('csvInput') csvInput: ElementRef<HTMLInputElement>;
  @ViewChild('mapContainer') mapContainer: ElementRef<HTMLElement>;
  @ViewChild('availableListContainer') availableListContainer?: ElementRef<HTMLElement>;
  @ViewChild('deliverySuggestionListContainer') deliverySuggestionListContainer?: ElementRef<HTMLElement>;
  @ViewChild('deliverySuggestionInput') deliverySuggestionInput?: ElementRef<HTMLInputElement>;

  availableStops: DispatchStop[] = [];
  deliverySuggestionStops: DispatchStop[] = [];
  importPanelVisible = true;
  deliverySuggestionPanelVisible = false;
  teams: PlannerTeam[] = createDefaultPlannerTeams();
  private nextTeamId = 3;
  activeDetailTeamId: DispatchTeamId = 1;
  teamOverviewSidebarVisible = true;

  /** Für Tour berechnen / PDF / Kartenroute. */
  activeTourTeam: DispatchTeamId = 1;

  selectedStopId: string | null = null;
  /** Array statt Set – zuverlässige Change Detection in der Auftragsliste. */
  selectedStopIds: string[] = [];
  selectionRevision = 0;
  markerRefreshToken = 0;

  selectionTool: MapSelectionTool = 'pan';
  isDrawingSelection = false;
  selectionStart: MapPoint | null = null;
  selectionCurrent: MapPoint | null = null;
  selectionRect: MapRect | null = null;
  lassoPoints: MapPoint[] = [];
  mapSelectionOverlayLayout: Record<string, string> = {
    left: '0',
    top: '0',
    width: '100%',
    height: '100%'
  };

  csvImportError = '';
  deliverySuggestionImportError = '';
  rollerSoapLoading = false;

  /** Listenansicht: alle Infos oder nur KV, Kosten, Kontingent. */
  listViewMode: DispatchListViewMode = 'detail';

  branches: DispatchBranchOption[] = [];
  teamOptions: DispatchTeamBaseOption[] = [];
  selectedBranchId: number | null = null;
  selectedBranchLocation: DispatchBranchLocation | null = null;
  branchLocationLoading = false;
  plannerDataLoading = false;

  duplicateStopIds = new Set<string>();
  duplicateCalendarIds = new Set<number>();

  private allTeamBases: any[] = [];
  private pendingFilialeCode: string | null = null;

  centerLat = 51.1657;
  centerLng = 10.4515;
  zoom = 6;
  mapTypeId: DispatchMapTypeId = 'roadmap';
  mapTilt = 0;
  streetViewActive = false;
  markerInfoLabelsVisible = false;

  loadingGeocode = false;
  tourCalculating = false;
  autoDispatching = false;
  calendarAppointmentsLoading = false;

  readonly availableListId = 'dispatchAvailableList';
  readonly deliverySuggestionListId = 'dispatchDeliverySuggestionList';

  findTeam(teamId: DispatchTeamId): PlannerTeam | undefined {
    return this.teams.find(team => team.id === teamId);
  }

  getTeamPanelKey(teamId: DispatchTeamId): PlannerListPanelKey {
    return `team:${teamId}`;
  }

  getTeamListId(teamId: DispatchTeamId): string {
    return getTeamListId(teamId);
  }

  getTeamColor(teamId: DispatchTeamId): string {
    const index = this.teams.findIndex(team => team.id === teamId);
    return getPlannerTeamColor(index >= 0 ? index : 0).css;
  }

  getTeamColorStyle(teamId: DispatchTeamId): Record<string, string> {
    return { '--team-color': this.getTeamColor(teamId) };
  }

  getTeamCategoryBreakdown(teamId: DispatchTeamId): TeamCategoryCount[] {
    const team = this.findTeam(teamId);
    return team ? buildTeamCategoryBreakdown(team.stops) : [];
  }

  getTeamStopCount(teamId: DispatchTeamId): number {
    return this.getTeamStops(teamId).length;
  }

  getTeamCalendarCount(teamId: DispatchTeamId): number {
    return this.findTeam(teamId)?.calendarAppointments.length ?? 0;
  }

  selectTeamForDetail(teamId: DispatchTeamId): void {
    const team = this.findTeam(teamId);
    if (!team) {
      return;
    }
    this.activeDetailTeamId = teamId;
    team.detailPanelVisible = true;
    this.cdr.detectChanges();
  }

  addPlannerTeam(): void {
    const team = createPlannerTeam(this.nextTeamId++);
    this.teams = [...this.teams, team];
    this.activeDetailTeamId = team.id;
    team.detailPanelVisible = true;
    this.cdr.detectChanges();
  }

  removePlannerTeam(teamId: DispatchTeamId, event?: Event): void {
    event?.stopPropagation();
    if (this.teams.length <= 1) {
      return;
    }
    const team = this.findTeam(teamId);
    if (!team || team.stops.length || team.calendarAppointments.length) {
      this.toastr.warning('Nur leere Teams können entfernt werden.', 'Team');
      return;
    }
    this.teams = this.teams.filter(entry => entry.id !== teamId);
    if (this.activeTourTeam === teamId) {
      this.activeTourTeam = this.teams[0]?.id ?? 1;
      this.clearRoutePolyline();
    }
    if (this.activeDetailTeamId === teamId) {
      this.activeDetailTeamId = this.teams[0]?.id ?? 1;
    }
    this.cdr.detectChanges();
  }

  hasAnyListPanelVisible(): boolean {
    return this.importPanelVisible
      || this.deliverySuggestionPanelVisible
      || this.teams.some(team => team.detailPanelVisible);
  }

  isListPanelVisible(panel: PlannerListPanelKey): boolean {
    if (panel === 'import') {
      return this.importPanelVisible;
    }
    if (panel === 'deliverySuggestion') {
      return this.deliverySuggestionPanelVisible;
    }
    const teamId = this.resolveTeamIdFromPanelKey(panel);
    return teamId != null ? !!this.findTeam(teamId)?.detailPanelVisible : false;
  }

  toggleListPanel(panel: PlannerListPanelKey): void {
    if (panel === 'import') {
      this.importPanelVisible = !this.importPanelVisible;
    } else if (panel === 'deliverySuggestion') {
      this.deliverySuggestionPanelVisible = !this.deliverySuggestionPanelVisible;
    } else {
      const teamId = this.resolveTeamIdFromPanelKey(panel);
      const team = teamId != null ? this.findTeam(teamId) : undefined;
      if (team) {
        team.detailPanelVisible = !team.detailPanelVisible;
        if (team.detailPanelVisible) {
          this.activeDetailTeamId = team.id;
        }
      }
    }
    this.cdr.detectChanges();
  }

  private resolveTeamIdFromPanelKey(panel: PlannerListPanelKey): DispatchTeamId | null {
    const match = /^team:(\d+)$/.exec(panel);
    return match ? Number(match[1]) : null;
  }

  readonly markerIconSize = { width: 42, height: 54 };
  readonly markerIconAnchor = { x: 21, y: 54 };
  readonly branchInnerRadiusKm = BRANCH_INNER_RADIUS_KM;
  readonly branchRadiusKm = BRANCH_FREE_RADIUS_KM;

  mapReady = false;

  private googleMap: google.maps.Map | null = null;
  private nativeMarkers: google.maps.Marker[] = [];
  private markerInfoOverlays: google.maps.OverlayView[] = [];
  private branchInnerRadiusCircle: google.maps.Circle | null = null;
  private branchRadiusCircle: google.maps.Circle | null = null;
  private routePolyline: google.maps.Polyline | null = null;
  private branchStopPolyline: google.maps.Polyline | null = null;
  private mapProjection = new DispatchMapProjection();
  private geocodeSubscription: Subscription | null = null;

  constructor(
    private dialog: MatDialog,
    private geocodingService: GeocodingService,
    private csvService: DispatchPlannerCsvService,
    private rollerSoapService: DispatchPlannerRollerSoapService,
    private deliverySuggestionService: DispatchPlannerDeliverySuggestionService,
    private orderService: OrderService,
    private customerBranchService: CustomerBranchService,
    private pdfService: DispatchPlannerPdfService,
    private plannerTeamService: DispatchPlannerTeamService,
    private autoDispatchService: DispatchPlannerAutoDispatchService,
    private plannerCalendarService: DispatchPlannerCalendarService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.renderer.addClass(document.body, 'mh3-body-scroll-lock');
    this.loadPlannerReferenceData();
  }

  get selectedBranchLabel(): string {
    return this.plannerTeamService.getBranchLabel(this.selectedBranchId, this.branches);
  }

  getTeamBaseIdForSlot(slot: DispatchTeamId): number | null {
    return this.findTeam(slot)?.teamBaseId ?? null;
  }

  getTeamBaseLabelForSlot(slot: DispatchTeamId): string {
    const id = this.getTeamBaseIdForSlot(slot);
    if (id == null) {
      return '';
    }
    return this.getTeamBaseLabelById(id);
  }

  getTeamBaseLabelById(teamBaseId: number): string {
    return this.teamOptions.find(t => t.id === teamBaseId)?.label || '';
  }

  /** Monteur-Teams, die noch keiner anderen Liste zugeordnet sind (aktuelle Liste ausgenommen). */
  getTeamBaseOptionsForSlot(slot: DispatchTeamId): DispatchTeamBaseOption[] {
    const assignedElsewhere = new Set(
      this.teams
        .filter(team => team.id !== slot && team.teamBaseId != null)
        .map(team => Number(team.teamBaseId))
    );
    return this.teamOptions.filter(option => !assignedElsewhere.has(option.id));
  }

  isTeamBaseAlreadyAssigned(teamBaseId: number, exceptSlot: DispatchTeamId): boolean {
    return this.teams.some(
      team => team.id !== exceptSlot && team.teamBaseId != null && Number(team.teamBaseId) === teamBaseId
    );
  }

  compareBranchId = (value: number | null, optionId: number | null): boolean =>
    value != null && optionId != null ? Number(value) === Number(optionId) : value === optionId;

  onBranchSelected(branchId: number | null): void {
    const normalizedBranchId = branchId != null ? Number(branchId) : null;
    const branchChanged = this.selectedBranchId !== normalizedBranchId;

    this.selectedBranchId = normalizedBranchId;
    this.refreshTeamOptionsForBranch();

    if (normalizedBranchId == null) {
      this.selectedBranchLocation = null;
      this.teams.forEach(team => {
        team.teamBaseId = null;
      });
      this.clearBranchToStopLine();
      this.refreshMapMarkers(true);
      this.refreshTeamCalendarAppointments();
      this.recalculateToursForTeams(this.teams.map(team => team.id));
      this.cdr.detectChanges();
      return;
    }

    if (branchChanged) {
      this.teams.forEach(team => {
        team.teamBaseId = null;
      });
    }

    if (branchChanged || !this.hasBranchCoords()) {
      this.selectedBranchLocation = null;
      this.loadSelectedBranchDetails(normalizedBranchId);
    }

    this.cdr.detectChanges();
  }

  hasBranchWaypoint(): boolean {
    return this.getBranchWaypoint() != null;
  }

  getTourLegBeforeStop(team: DispatchTeamId, stopIndex: number): TourRouteLeg | null {
    const legs = this.getTourLegs(team);
    if (!legs.length || stopIndex < 0) {
      return null;
    }
    const legIndex = this.hasBranchWaypoint() ? stopIndex : stopIndex - 1;
    if (legIndex < 0) {
      return null;
    }
    return legs[legIndex] ?? null;
  }

  private loadSelectedBranchDetails(branchId: number): void {
    this.branchLocationLoading = true;
    this.customerBranchService.show(branchId).subscribe({
      next: (branch: any) => {
        this.ngZone.run(() => {
          this.selectedBranchLocation = this.normalizeBranchLocation(branch);
          if (!this.hasBranchCoords()) {
            this.geocodeSelectedBranch(branchId);
            return;
          }
          this.branchLocationLoading = false;
          this.onBranchLocationReady();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.branchLocationLoading = false;
          this.selectedBranchLocation = null;
          this.toastr.warning('Filialdaten konnten nicht geladen werden.', 'Filiale');
          this.cdr.detectChanges();
        });
      }
    });
  }

  private geocodeSelectedBranch(branchId: number): void {
    this.customerBranchService.geocodeBranchCoords(branchId).subscribe({
      next: (res: any) => {
        this.ngZone.run(() => {
          if (this.selectedBranchLocation?.id !== branchId) {
            this.branchLocationLoading = false;
            return;
          }
          const lat = res?.lat ?? res?.data?.lat ?? null;
          const lng = res?.lng ?? res?.data?.lng ?? null;
          this.selectedBranchLocation = {
            ...this.selectedBranchLocation,
            lat: lat != null ? Number(lat) : null,
            lng: lng != null ? Number(lng) : null
          };
          this.branchLocationLoading = false;
          if (!this.hasBranchCoords()) {
            this.toastr.warning('Koordinaten der Filiale konnten nicht ermittelt werden.', 'Filiale');
          }
          this.onBranchLocationReady();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.branchLocationLoading = false;
          this.toastr.warning('Koordinaten der Filiale konnten nicht ermittelt werden.', 'Filiale');
          this.onBranchLocationReady();
        });
      }
    });
  }

  private onBranchLocationReady(): void {
    this.refreshMapMarkers(true);
    this.refreshTeamCalendarAppointments();
    this.recalculateToursForTeams(this.teams.map(team => team.id));
    this.updateBranchToStopLine();
    this.cdr.detectChanges();
  }

  private normalizeBranchLocation(branch: any): DispatchBranchLocation {
    const id = Number(branch?.id);
    const lat = branch?.lat != null ? Number(branch.lat) : null;
    const lng = branch?.lng != null ? Number(branch.lng) : null;
    return {
      id,
      label: this.plannerTeamService.getBranchLabel(id, this.branches),
      lat: lat != null && Number.isFinite(lat) ? lat : null,
      lng: lng != null && Number.isFinite(lng) ? lng : null,
      street: branch?.street,
      postal_code: branch?.postal_code,
      place: branch?.place,
      title: branch?.title
    };
  }

  private hasBranchCoords(): boolean {
    const loc = this.selectedBranchLocation;
    return loc != null && loc.lat != null && loc.lng != null && Number.isFinite(loc.lat) && Number.isFinite(loc.lng);
  }

  private getBranchWaypoint(): { lat: number; lng: number } | null {
    if (!this.hasBranchCoords() || !this.selectedBranchLocation) {
      return null;
    }
    return {
      lat: Number(this.selectedBranchLocation.lat),
      lng: Number(this.selectedBranchLocation.lng)
    };
  }

  private buildTeamRouteWaypoints(stops: DispatchStop[]): { lat: number; lng: number }[] {
    const stopPoints = stops
      .filter(s => s.lat != null && s.lng != null && !s.geocodingFailed)
      .map(s => ({ lat: Number(s.lat), lng: Number(s.lng) }));
    const branch = this.getBranchWaypoint();
    if (!branch || !stopPoints.length) {
      return stopPoints;
    }
    return [branch, ...stopPoints, branch];
  }

  private getMinStopsForTour(): number {
    return this.hasBranchWaypoint() ? 1 : 2;
  }

  onTeamBaseSelected(slot: DispatchTeamId, teamBaseId: number | null): void {
    const team = this.findTeam(slot);
    if (!team) {
      return;
    }

    const normalizedTeamBaseId = teamBaseId != null ? Number(teamBaseId) : null;
    if (normalizedTeamBaseId != null && this.isTeamBaseAlreadyAssigned(normalizedTeamBaseId, slot)) {
      const previousOwner = this.teams.find(
        entry => entry.id !== slot && Number(entry.teamBaseId) === normalizedTeamBaseId
      );
      if (previousOwner) {
        previousOwner.teamBaseId = null;
        previousOwner.calendarAppointments = [];
        this.toastr.info(
          `${this.getTeamBaseLabelById(normalizedTeamBaseId)} wurde von „${previousOwner.label}“ nach „${team.label}“ verschoben.`,
          'Monteur-Team'
        );
      }
    }

    team.teamBaseId = normalizedTeamBaseId;
    if (!normalizedTeamBaseId) {
      team.calendarAppointments = [];
    }
    this.refreshTeamCalendarAppointments();
    this.cdr.detectChanges();
  }

  getTeamCalendarAppointments(team: DispatchTeamId): DispatchCalendarAppointment[] {
    return this.findTeam(team)?.calendarAppointments ?? [];
  }

  getPlanningDateLabel(): string {
    const date = this.resolvePlanningDate();
    if (!date) {
      return '';
    }
    const [y, m, d] = date.split('-');
    return `${d}.${m}.${y}`;
  }

  trackCalendarAppointment(_index: number, item: DispatchCalendarAppointment): number {
    return item.id;
  }

  private resolvePlanningDate(): string | null {
    return this.plannerCalendarService.resolvePlanningDate([
      ...this.availableStops,
      ...this.deliverySuggestionStops,
      ...this.allTeamStops()
    ]);
  }

  allTeamStops(): DispatchStop[] {
    return this.teams.reduce<DispatchStop[]>((acc, team) => acc.concat(team.stops), []);
  }

  private refreshTeamCalendarAppointments(): void {
    const planningDate = this.resolvePlanningDate();
    if (!planningDate) {
      this.teams.forEach(team => {
        team.calendarAppointments = [];
      });
      this.refreshMapMarkers(false);
      this.cdr.detectChanges();
      return;
    }

    const requests: Array<{ team: DispatchTeamId; teamBaseId: number }> = [];
    this.teams.forEach(team => {
      if (team.teamBaseId) {
        requests.push({ team: team.id, teamBaseId: team.teamBaseId });
      } else {
        team.calendarAppointments = [];
      }
    });

    if (!requests.length) {
      this.cdr.detectChanges();
      return;
    }

    this.calendarAppointmentsLoading = true;
    let pending = requests.length;

    requests.forEach(({ team, teamBaseId }) => {
      this.plannerCalendarService
        .loadTeamAppointments(teamBaseId, planningDate, this.selectedBranchId)
        .subscribe({
          next: appointments => {
            this.ngZone.run(() => {
              const plannerTeam = this.findTeam(team);
              if (plannerTeam) {
                plannerTeam.calendarAppointments = appointments.map(a => ({ ...a, team }));
              }
              pending--;
              if (pending <= 0) {
                this.calendarAppointmentsLoading = false;
                this.markerRefreshToken++;
                this.refreshDuplicateFlags();
                this.refreshMapMarkers(true);
              }
              this.cdr.detectChanges();
            });
          },
          error: () => {
            this.ngZone.run(() => {
              const plannerTeam = this.findTeam(team);
              if (plannerTeam) {
                plannerTeam.calendarAppointments = [];
              }
              pending--;
              if (pending <= 0) {
                this.calendarAppointmentsLoading = false;
              }
              this.cdr.detectChanges();
            });
          }
        });
    });
  }

  private loadPlannerReferenceData(): void {
    this.plannerDataLoading = true;
    this.plannerTeamService.loadPlannerReferenceData().subscribe({
      next: ({ branches, teamBases }) => {
        this.ngZone.run(() => {
          this.branches = branches;
          this.allTeamBases = teamBases;
          this.refreshTeamOptionsForBranch();
          this.plannerDataLoading = false;
          if (!branches.length) {
            this.toastr.warning(
              'Keine Filialen geladen – Filiale bitte manuell wählen oder Seite neu laden.',
              'Stammdaten'
            );
          }
          this.tryApplyPendingFiliale();
          this.tryApplyFilialeFromImportedStops();
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.plannerDataLoading = false;
          this.toastr.warning('Filialen und Teams konnten nicht geladen werden.', 'Stammdaten');
          this.cdr.detectChanges();
        });
      }
    });
  }

  private refreshTeamOptionsForBranch(): void {
    if (this.selectedBranchId == null) {
      this.teamOptions = [];
      return;
    }
    this.teamOptions = this.plannerTeamService.getTeamsForBranch(
      this.selectedBranchId,
      this.allTeamBases
    );
  }

  private applyFilialeFromImport(stations: DispatchCsvStation[], csvContent?: string): void {
    const codes = this.csvService.collectBranchHints(stations, csvContent);
    if (!codes.length) {
      this.toastr.info(
        'Keine Filiale erkannt (weder aus den ersten 2 Ziffern der KV-Nummer noch aus einer Filial-Spalte). Bitte manuell wählen.',
        'CSV'
      );
      return;
    }
    if (codes.length > 1) {
      this.toastr.warning(
        `Mehrere Filialen im Import (${codes.join(', ')}). Bitte Filiale manuell wählen.`,
        'CSV'
      );
      return;
    }

    const code = codes[0];
    if (this.applyFilialeCode(code, { showSuccessToast: true })) {
      return;
    }

    if (!this.branches.length || this.plannerDataLoading) {
      this.pendingFilialeCode = code;
      this.tryApplyPendingFiliale();
      return;
    }

    this.toastr.warning(
      `Filiale „${code}“ konnte keiner Filiale in MH3 zugeordnet werden.` +
        (code.includes('@') ? ' (E-Mail-Abgleich mit customer_branch)' : ''),
      'CSV'
    );
  }

  private tryApplyPendingFiliale(): void {
    if (!this.pendingFilialeCode || !this.branches.length || this.plannerDataLoading) {
      return;
    }

    const code = this.pendingFilialeCode;
    if (this.applyFilialeCode(code, { showSuccessToast: true })) {
      return;
    }

    this.pendingFilialeCode = null;
    this.toastr.warning(
      `Filiale „${code}“ konnte keiner Filiale in MH3 zugeordnet werden.` +
        (code.includes('@') ? ' (E-Mail-Abgleich mit customer_branch)' : ''),
      'CSV'
    );
  }

  private tryApplyFilialeFromImportedStops(): void {
    if (this.selectedBranchId != null || this.pendingFilialeCode || !this.branches.length) {
      return;
    }
    const code = this.csvService.resolveSingleBranchHint(
      [...this.availableStops, ...this.deliverySuggestionStops].map(stop => stop.kaufvertragsnummer || ''),
      [...this.availableStops, ...this.deliverySuggestionStops].map(stop => stop.filiale || ''),
      [...this.availableStops, ...this.deliverySuggestionStops].map(stop => stop.email || '')
    );
    if (!code) {
      return;
    }
    this.applyFilialeCode(code, { showSuccessToast: false });
  }

  private applyFilialeCode(
    code: string,
    options: { showSuccessToast?: boolean } = {}
  ): boolean {
    const branchId = this.plannerTeamService.resolveBranchId(code, this.branches);
    if (branchId == null) {
      return false;
    }

    this.pendingFilialeCode = null;
    const branchChanged = this.selectedBranchId !== branchId;
    this.selectedBranchId = Number(branchId);
    this.refreshTeamOptionsForBranch();

    if (branchChanged) {
      this.teams.forEach(team => {
        team.teamBaseId = null;
      });
    }

    if (branchChanged || !this.hasBranchCoords()) {
      this.selectedBranchLocation = null;
      this.loadSelectedBranchDetails(branchId);
    }

    if (options.showSuccessToast) {
      const label = this.plannerTeamService.getBranchLabel(branchId, this.branches);
      const viaKv = /^\d{2}$/.test(code.trim());
      this.toastr.success(
        viaKv
          ? `Filiale „${label}“ aus KV-Präfix „${code}“ erkannt.`
          : `Filiale „${label}“ aus CSV übernommen.`,
        'CSV'
      );
    }

    this.cdr.detectChanges();
    return true;
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'mh3-body-scroll-lock');
    this.geocodeSubscription?.unsubscribe();
    this.clearNativeMarkers();
    this.clearBranchRadiusCircle();
    this.clearRoutePolyline();
    this.clearBranchToStopLine();
    this.mapProjection.detach();
  }

  get lassoSvgPoints(): string {
    return pointsToSvgPoints(this.lassoPointsForDisplay);
  }

  get selectionRectForDisplay(): MapRect | null {
    return this.selectionRect;
  }

  /** Lasso-Punkte in Map-Container-Koordinaten für die SVG-Overlay-Anzeige. */
  get lassoPointsForDisplay(): MapPoint[] {
    return this.lassoPoints;
  }

  get totalStopCount(): number {
    return this.availableStops.length + this.deliverySuggestionStops.length + this.allTeamStops().length;
  }

  get tourDistance(): string {
    return this.findTeam(this.activeTourTeam)?.tour.distance ?? '';
  }

  get tourDuration(): string {
    return this.findTeam(this.activeTourTeam)?.tour.duration ?? '';
  }

  get tourRouteLegs(): TourRouteLeg[] {
    return this.findTeam(this.activeTourTeam)?.tour.legs ?? [];
  }

  get tourRoutePath(): { lat: number; lng: number }[] {
    return this.findTeam(this.activeTourTeam)?.tour.path ?? [];
  }

  get activeTeamStops(): DispatchStop[] {
    return this.getTeamStops(this.activeTourTeam);
  }

  get teamKostenGesamt(): number {
    return this.teams.reduce((sum, team) => sum + this.sumKosten(team.stops), 0);
  }

  getTeamUmsatz(team: DispatchTeamId): number {
    return this.sumKosten(this.getTeamStops(team));
  }

  isTeamTourCalculating(team: DispatchTeamId): boolean {
    return !!this.findTeam(team)?.tourCalculating;
  }

  getTourDistanceForTeam(team: DispatchTeamId): string {
    return this.findTeam(team)?.tour.distance ?? '';
  }

  getTourDurationForTeam(team: DispatchTeamId): string {
    return this.findTeam(team)?.tour.duration ?? '';
  }

  getTeamStops(team: DispatchTeamId): DispatchStop[] {
    return this.findTeam(team)?.stops ?? [];
  }

  getTourLegs(team: DispatchTeamId): TourRouteLeg[] {
    return this.findTeam(team)?.tour.legs ?? [];
  }

  private sumKosten(stops: DispatchStop[]): number {
    return stops.reduce((sum, s) => sum + this.getStopKostenGesamt(s), 0);
  }

  hasKostenInfo(stop: DispatchStop): boolean {
    return this.getStopKostenGesamt(stop) > 0 || this.getStopBaseKosten(stop) > 0;
  }

  getStopServiceTypeFullLabel(stop: DispatchStop): string {
    return getStopServiceTypeFullLabel(stop);
  }

  getStopFilialeLabel(stop: DispatchStop): string {
    const filiale = (stop.filiale || '').trim();
    const bezeichnung = (stop.filialbezeichnung || stop.tourBezeichnung || '').trim();
    if (filiale && bezeichnung) {
      return `${filiale} [${bezeichnung}]`;
    }
    return filiale || bezeichnung;
  }

  getStopPrimaryArticleLabel(stop: DispatchStop): string {
    return getStopPrimaryArticleLabel(stop);
  }

  hasStopGewicht(stop: DispatchStop): boolean {
    return (stop.gewichtKg ?? 0) > 0;
  }

  hasStopVolumen(stop: DispatchStop): boolean {
    return (stop.volumenM3 ?? 0) > 0;
  }

  formatGewicht(stop: DispatchStop): string {
    const kg = stop.gewichtKg ?? 0;
    if (!kg) {
      return '';
    }
    return `${kg.toLocaleString('de-DE', { maximumFractionDigits: 2 })} kg`;
  }

  formatVolumen(stop: DispatchStop): string {
    const m3 = stop.volumenM3 ?? 0;
    if (!m3) {
      return '';
    }
    return `${m3.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m³`;
  }

  getStopBaseKosten(stop: DispatchStop): number {
    return calculateStopBaseKosten(stop);
  }

  getStopDistanceSurcharge(stop: DispatchStop): number {
    return calculateDistanceSurchargeEur(this.getStopDistanceFromBranchKm(stop));
  }

  getStopKostenGesamt(stop: DispatchStop): number {
    return calculateStopTotalKosten(stop, this.getStopDistanceFromBranchKm(stop));
  }

  hasArticles(stop: DispatchStop): boolean {
    return (stop.articles?.length ?? 0) > 0 || (stop.artikelCount ?? 0) > 0;
  }

  formatCurrency(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
      return '–';
    }
    return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }

  setListViewMode(mode: DispatchListViewMode): void {
    this.listViewMode = mode;
    this.cdr.detectChanges();
  }

  isCompactListView(): boolean {
    return this.listViewMode === 'compact';
  }

  /** Montagezeit: Montageeinheiten × 5 Minuten. */
  formatMontageZeit(stop: DispatchStop): string {
    const units = stop.montagemenge ?? 0;
    if (!units) {
      return '–';
    }
    const minutes = Math.round(units * 5);
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest ? `${hours} h ${rest} min` : `${hours} h`;
  }

  hasCompactKostenValue(value: number | null | undefined): boolean {
    return value != null && !Number.isNaN(value) && value !== 0;
  }

  hasCompactMontageZeit(stop: DispatchStop): boolean {
    return (stop.montagemenge ?? 0) > 0;
  }

  formatCompactKosten(value: number | null | undefined): string {
    if (!this.hasCompactKostenValue(value)) {
      return '–';
    }
    return this.formatCurrency(value);
  }

  formatCompactGesamt(stop: DispatchStop): string {
    return this.formatCompactKosten(this.getStopKostenGesamt(stop));
  }

  formatStopTermin(termin?: string): string {
    return formatDispatchTermin(termin);
  }

  getStopNachnameLabel(stop: DispatchStop): string {
    return getStopNachname(stop);
  }

  getStopPlzLabel(stop: DispatchStop): string {
    return getStopPlz(stop);
  }

  getStopOrtLabel(stop: DispatchStop): string {
    return getStopOrt(stop);
  }

  getStopServiceTypeCompactLabel(stop: DispatchStop): string {
    return getStopServiceTypeLabel(stop);
  }

  formatCompactMontageMenge(stop: DispatchStop): string {
    const units = stop.montagemenge ?? 0;
    if (!units) {
      return '';
    }
    return `${units.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ME`;
  }

  hasCompactServiceInfo(stop: DispatchStop): boolean {
    return !!this.getStopServiceTypeCompactLabel(stop)
      || !!this.formatCompactMontageMenge(stop)
      || this.hasCompactMontageZeit(stop)
      || this.hasKostenInfo(stop);
  }

  getKontingentKurzLabel(stop: DispatchStop): string {
    if (stop.source === 'delivery-suggestion') {
      return this.getDeliverySuggestionListLabel(stop);
    }
    switch (stop.kontingent) {
      case 'kueche':
        return 'Küche';
      case 'moebel':
        return 'Möbel';
      default:
        return stop.kontingentLabel || 'Sonstiges';
    }
  }

  openArticlesDialog(stop: DispatchStop, event?: Event): void {
    event?.stopPropagation();
    if (!this.hasArticles(stop)) {
      return;
    }
    this.dialog.open(DispatchPlannerArticlesDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: {
        stop,
        kostenBase: this.getStopBaseKosten(stop),
        distanceSurcharge: this.getStopDistanceSurcharge(stop),
        kostenGesamt: this.getStopKostenGesamt(stop)
      } as DispatchPlannerArticlesDialogData
    });
  }

  get selectedAvailableCount(): number {
    const selected = new Set(this.selectedStopIds);
    return this.availableStops.filter(s => selected.has(s.id)).length;
  }

  get selectedDeliverySuggestionCount(): number {
    const selected = new Set(this.selectedStopIds);
    return this.deliverySuggestionStops.filter(s => selected.has(s.id)).length;
  }

  isDeliverySuggestionStop(stop: DispatchStop): boolean {
    return stop.source === 'delivery-suggestion' && this.getStopTeam(stop) == null;
  }

  getDeliverySuggestionAuftragsartLabel(stop: DispatchStop): string {
    return (stop.auftragsart || 'M').trim().toUpperCase();
  }

  getDeliverySuggestionListLabel(stop: DispatchStop): string {
    if ((stop.montageFlag || '').trim().toUpperCase() === 'J') {
      const me = stop.montagemenge ?? 0;
      return me > 0 ? `Montage ${me} ME` : 'Montage';
    }
    return this.getDeliverySuggestionAuftragsartLabel(stop);
  }

  private buildDeliverySuggestionKontingentLabel(row: DeliverySuggestionRow): string {
    if ((row.montage || '').trim().toUpperCase() === 'J') {
      return row.montagemenge > 0 ? `Montage ${row.montagemenge} ME` : 'Montage';
    }
    return (row.auftragsart || 'M').trim().toUpperCase();
  }

  getStopHomeListKey(stop: DispatchStop): PlannerListKey {
    return stop.source === 'delivery-suggestion' ? 'delivery-suggestion' : 'available';
  }

  canAutoDispatch(): boolean {
    return this.canAutoDispatchFromSource('available');
  }

  canAutoDispatchDeliverySuggestion(): boolean {
    return this.canAutoDispatchFromSource('delivery-suggestion');
  }

  private canAutoDispatchFromSource(source: 'available' | 'delivery-suggestion'): boolean {
    return (
      !this.autoDispatching &&
      !this.branchLocationLoading &&
      this.teams.length > 0 &&
      this.getMappableStopsFromSource(source).length > 0
    );
  }

  autoDispatchStops(): void {
    this.runAutoDispatchFromSource('available');
  }

  autoDispatchDeliverySuggestionStops(): void {
    this.runAutoDispatchFromSource('delivery-suggestion');
  }

  private runAutoDispatchFromSource(source: 'available' | 'delivery-suggestion'): void {
    const sourceLabel = source === 'delivery-suggestion' ? 'Liefervorschlag' : 'Import';
    const sourceStops = source === 'delivery-suggestion'
      ? [...this.deliverySuggestionStops]
      : [...this.availableStops];
    const mappableStops = this.getMappableStopsFromSource(source);

    if (!mappableStops.length) {
      this.toastr.warning(
        `Keine Aufträge mit Koordinaten in der ${sourceLabel}liste.`,
        'Automatisch disponieren'
      );
      return;
    }
    if (!this.hasBranchWaypoint()) {
      this.toastr.info(
        'Ohne Filial-Koordinaten wird nur nach Entfernung zwischen Aufträgen optimiert.',
        'Automatisch disponieren'
      );
    }

    this.autoDispatching = true;
    const result = this.autoDispatchService.dispatch({
      importStops: sourceStops,
      initialTeams: this.teams.map(team => [...team.stops]),
      branch: this.getBranchWaypoint(),
      maxRadiusKm: this.hasBranchWaypoint() ? this.branchRadiusKm : undefined
    });

    result.teams.forEach((stops, index) => {
      const team = this.teams[index];
      if (team) {
        this.setTeamStops(team.id, stops as DispatchStop[]);
      }
    });

    if (source === 'delivery-suggestion') {
      this.deliverySuggestionStops = result.remainingImport as DispatchStop[];
    } else {
      this.availableStops = result.remainingImport as DispatchStop[];
    }

    this.reindexTeams();
    this.teams = [...this.teams];
    this.clearMapSelection();
    this.refreshMapMarkers(true);
    this.autoDispatching = false;

    const skipped = sourceStops.length - result.assignedCount;
    const teamSummary = this.teams
      .map(team => `${team.label}: ${team.stops.length}`)
      .join(' · ');
    const msg = [`${result.assignedCount} Aufträge disponiert`, teamSummary].filter(Boolean).join(' · ');
    const remainingHint = source === 'delivery-suggestion' ? 'verbleiben im Liefervorschlag' : 'verbleiben im Import';

    if (skipped > 0) {
      const hints: string[] = [];
      if (result.outsideRadiusCount > 0) {
        hints.push(`${result.outsideRadiusCount} außerhalb ${this.branchRadiusKm} km`);
      }
      if (result.withoutCoordsCount > 0) {
        hints.push(`${result.withoutCoordsCount} ohne Koordinaten`);
      }
      const suffix = hints.length ? ` (${hints.join(', ')} ${remainingHint})` : '';
      this.toastr.warning(`${msg}${suffix}.`, 'Automatisch disponieren');
    } else {
      this.toastr.success(msg, `Automatisch disponieren (${sourceLabel})`);
    }

    this.finishAutoDispatchTours();
    this.refreshDuplicateFlags();
    this.cdr.detectChanges();
  }

  private getMappableStopsFromSource(source: 'available' | 'delivery-suggestion'): DispatchStop[] {
    const stops = source === 'delivery-suggestion'
      ? this.deliverySuggestionStops
      : this.availableStops;
    return stops.filter(
      s =>
        s.lat != null &&
        s.lng != null &&
        !s.geocodingFailed &&
        Number.isFinite(Number(s.lat)) &&
        Number.isFinite(Number(s.lng))
    );
  }

  private finishAutoDispatchTours(): void {
    this.teams.forEach(team => {
      if (this.canOptimizeTour(team.id)) {
        this.optimizeTour(team.id);
      } else if (this.getMappableTeamStops(team.id).length >= this.getMinStopsForTour()) {
        this.calculateTourForTeam(team.id, { drawOnMap: team.id === this.activeTourTeam });
      }
    });
  }

  isDuplicateStop(stop: DispatchStop): boolean {
    return this.duplicateStopIds.has(stop.id);
  }

  isDuplicateCalendarAppointment(appointment: DispatchCalendarAppointment): boolean {
    return this.duplicateCalendarIds.has(appointment.id);
  }

  getStopDuplicateLabel(stop: DispatchStop): string {
    const kv = (stop.kaufvertragsnummer || '').trim();
    if (kv) {
      return `KV ${kv}`;
    }
    return stop.customerName || stop.address || 'Auftrag';
  }

  private getStopDuplicateKey(stop: DispatchStop): string {
    const kv = (stop.kaufvertragsnummer || '').trim().toLowerCase();
    if (kv) {
      return `kv:${kv}`;
    }
    const address = (stop.address || '').trim().toLowerCase();
    const customer = (stop.customerName || '').trim().toLowerCase();
    if (address) {
      return `addr:${customer}|${address}`;
    }
    return `id:${stop.id}`;
  }

  private getCalendarDuplicateKey(appointment: DispatchCalendarAppointment): string | null {
    const kv = (appointment.orderNumber || '').trim().toLowerCase();
    return kv ? `kv:${kv}` : null;
  }

  private refreshDuplicateFlags(): void {
    this.duplicateStopIds = new Set<string>();
    this.duplicateCalendarIds = new Set<number>();
    this.teams.forEach(team => this.markTeamDuplicates(team.id));
  }

  private markTeamDuplicates(team: DispatchTeamId): void {
    const buckets = new Map<string, { stopIds: string[]; calendarIds: number[] }>();

    const addEntry = (key: string, stopId?: string, calendarId?: number) => {
      if (!buckets.has(key)) {
        buckets.set(key, { stopIds: [], calendarIds: [] });
      }
      const bucket = buckets.get(key)!;
      if (stopId) {
        bucket.stopIds.push(stopId);
      }
      if (calendarId != null) {
        bucket.calendarIds.push(calendarId);
      }
    };

    for (const stop of this.getTeamStops(team)) {
      addEntry(this.getStopDuplicateKey(stop), stop.id);
    }
    for (const appointment of this.getTeamCalendarAppointments(team)) {
      const key = this.getCalendarDuplicateKey(appointment);
      if (key) {
        addEntry(key, undefined, appointment.id);
      }
    }

    buckets.forEach(bucket => {
      const total = bucket.stopIds.length + bucket.calendarIds.length;
      if (total < 2) {
        return;
      }
      bucket.stopIds.forEach(id => this.duplicateStopIds.add(id));
      bucket.calendarIds.forEach(id => this.duplicateCalendarIds.add(id));
    });
  }

  private warnDuplicateInsert(stop: DispatchStop, team: DispatchTeamId): void {
    const key = this.getStopDuplicateKey(stop);
    let matches = 0;
    for (const item of this.getTeamStops(team)) {
      if (this.getStopDuplicateKey(item) === key) {
        matches++;
      }
    }
    for (const appointment of this.getTeamCalendarAppointments(team)) {
      const aptKey = this.getCalendarDuplicateKey(appointment);
      if (aptKey === key) {
        matches++;
      }
    }
    if (matches > 1) {
      this.toastr.warning(
        `${this.getStopDuplicateLabel(stop)} ist in Team ${team} mehrfach vorhanden.`,
        'Doppelter Auftrag'
      );
    }
  }

  trackStopById(_index: number, stop: DispatchStop): string {
    return stop.id;
  }

  get mappedCalendarAppointments(): DispatchCalendarAppointment[] {
    return this.teams
      .reduce<DispatchCalendarAppointment[]>((acc, team) => acc.concat(team.calendarAppointments), [])
      .filter(a => a.lat != null && a.lng != null && Number.isFinite(Number(a.lat)) && Number.isFinite(Number(a.lng)));
  }

  get mappedStops(): DispatchStop[] {
    return [...this.availableStops, ...this.deliverySuggestionStops, ...this.allTeamStops()].filter(
      s => s.lat != null && s.lng != null && !s.geocodingFailed
    );
  }

  getMarkerIconUrl(stop: DispatchStop): string {
    const team = this.getStopTeam(stop);
    const onTeam = team != null;
    const label =
      onTeam && stop.orderIndex > 0 ? `${stop.orderIndex}` : '';
    const selected = this.isMapSelected(stop);
    const focused = this.isMarkerFocused(stop);
    const duplicate = this.isDuplicateStop(stop);
    if (this.isDeliverySuggestionStop(stop)) {
      return buildDeliverySuggestionMarkerIcon(
        this.getDeliverySuggestionAuftragsartLabel(stop),
        selected,
        duplicate,
        focused
      );
    }
    if (label || selected || focused || duplicate) {
      const teamIndex = team != null ? this.teams.findIndex(entry => entry.id === team) : -1;
      return buildDispatchMarkerIcon(
        label,
        stop.kontingent || 'other',
        !onTeam,
        selected,
        teamIndex >= 0 ? teamIndex : undefined,
        duplicate,
        focused
      );
    }
    return getDefaultMarkerIconUrl(stop.kontingent || 'other', selected);
  }

  private getMarkerZIndex(stop: DispatchStop): number | undefined {
    if (this.isMarkerFocused(stop)) {
      return google.maps.Marker.MAX_ZINDEX + 50;
    }
    if (this.isMapSelected(stop)) {
      return google.maps.Marker.MAX_ZINDEX + 20;
    }
    return undefined;
  }

  getMarkerLabel(stop: DispatchStop): string | null {
    const team = this.getStopTeam(stop);
    if (team != null && stop.orderIndex > 0) {
      return `${stop.orderIndex}`;
    }
    return null;
  }

  getStopTeam(stop: DispatchStop): DispatchTeamId | null {
    if (stop.teamNumber != null && this.findTeam(stop.teamNumber)) {
      return stop.teamNumber;
    }
    const team = this.teams.find(entry => entry.stops.some(item => item.id === stop.id));
    return team?.id ?? null;
  }

  isOnTeam(stop: DispatchStop): boolean {
    return this.getStopTeam(stop) != null;
  }

  setActiveTourTeam(team: DispatchTeamId): void {
    this.activeTourTeam = team;
    const path = this.findTeam(team)?.tour.path ?? [];
    if (path.length >= 2) {
      this.drawRoutePolyline(path, team);
    } else {
      this.clearRoutePolyline();
    }
    this.cdr.detectChanges();
  }

  isMapSelected(stop: DispatchStop): boolean {
    return this.selectedStopIds.includes(stop.id);
  }

  isMarkerFocused(stop: DispatchStop): boolean {
    return this.selectedStopId === stop.id;
  }

  isListActive(stop: DispatchStop): boolean {
    return this.isMarkerFocused(stop) || this.isMapSelected(stop);
  }

  getKontingentClass(stop: DispatchStop): string {
    switch (stop.kontingent) {
      case 'kueche': return 'badge-kueche';
      case 'moebel': return 'badge-moebel';
      default: return 'badge-other';
    }
  }

  onMapReady(map: google.maps.Map): void {
    this.googleMap = map;
    this.mapReady = true;
    this.mapProjection.attach(map);
    this.applyMapInteractionMode();
    this.applyMapDisplayOptions();
    const streetView = map.getStreetView();
    streetView.setOptions({ enableCloseButton: true });
    google.maps.event.addListener(streetView, 'visible_changed', () => {
      this.ngZone.run(() => {
        this.streetViewActive = streetView.getVisible();
        this.cdr.detectChanges();
      });
    });
    google.maps.event.addListenerOnce(map, 'idle', () => {
      this.ngZone.run(() => {
        this.updateMapSelectionOverlayLayout();
        this.refreshMapMarkers(false);
        this.cdr.detectChanges();
      });
    });
    google.maps.event.addListener(map, 'idle', () => {
      this.ngZone.run(() => {
        this.updateMapSelectionOverlayLayout();
        this.cdr.markForCheck();
      });
    });
    this.updateMapSelectionOverlayLayout();
    this.ngZone.run(() => {
      this.refreshMapMarkers(false);
      this.cdr.detectChanges();
    });
  }

  setSelectionTool(tool: MapSelectionTool): void {
    this.cancelDrawing();
    this.selectionTool = tool;
    this.applyMapInteractionMode();
    this.updateMapSelectionOverlayLayout();
  }

  private updateMapSelectionOverlayLayout(): void {
    const mapDiv = this.googleMap?.getDiv();
    const container = this.mapContainer?.nativeElement;
    if (!mapDiv || !container) {
      this.mapSelectionOverlayLayout = {
        left: '0',
        top: '0',
        width: '100%',
        height: '100%'
      };
      return;
    }

    const mapRect = mapDiv.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    this.mapSelectionOverlayLayout = {
      left: `${mapRect.left - containerRect.left}px`,
      top: `${mapRect.top - containerRect.top}px`,
      width: `${mapRect.width}px`,
      height: `${mapRect.height}px`
    };
  }

  setMapType(type: DispatchMapTypeId): void {
    this.mapTypeId = type;
    if (type === 'roadmap') {
      this.mapTilt = 0;
    }
    this.applyMapDisplayOptions();
  }

  setMap2D(): void {
    this.mapTilt = 0;
    this.applyMapDisplayOptions();
  }

  setMap3D(): void {
    if (this.mapTypeId === 'roadmap') {
      this.mapTypeId = 'hybrid';
    }
    this.mapTilt = 45;
    if (this.zoom < 14) {
      this.zoom = 14;
      this.googleMap?.setZoom(14);
    }
    this.applyMapDisplayOptions();
  }

  toggleStreetView(): void {
    if (!this.googleMap) {
      return;
    }
    const streetView = this.googleMap.getStreetView();
    if (streetView.getVisible()) {
      streetView.setVisible(false);
      this.streetViewActive = false;
      return;
    }
    const position = this.getStreetViewTargetPosition();
    streetView.setPosition(position);
    streetView.setPov({ heading: 0, pitch: 0 });
    streetView.setVisible(true);
    this.streetViewActive = true;
  }

  toggleMarkerInfoLabels(): void {
    this.markerInfoLabelsVisible = !this.markerInfoLabelsVisible;
    this.refreshMapMarkers(false);
    this.cdr.detectChanges();
  }

  private getStreetViewTargetPosition(): google.maps.LatLngLiteral {
    if (this.selectedStopId) {
      const stop = this.findStop(this.selectedStopId);
      if (stop?.lat != null && stop?.lng != null) {
        return { lat: Number(stop.lat), lng: Number(stop.lng) };
      }
    }
    return { lat: this.centerLat, lng: this.centerLng };
  }

  private applyMapDisplayOptions(): void {
    if (!this.googleMap) {
      return;
    }
    this.googleMap.setOptions({
      mapTypeId: this.mapTypeId,
      tilt: this.mapTilt
    });
  }

  calculateTour(): void {
    this.calculateTourForTeam(this.activeTourTeam, { showWarnings: true });
  }

  canCalculateTour(team: DispatchTeamId): boolean {
    return this.getMappableTeamStops(team).length >= this.getMinStopsForTour();
  }

  canOptimizeTour(team: DispatchTeamId): boolean {
    return this.hasBranchWaypoint() && this.getMappableTeamStops(team).length >= 2;
  }

  optimizeTour(team: DispatchTeamId): void {
    const stops = this.getTeamStops(team);
    const mappable = this.getMappableTeamStops(team);
    const branch = this.getBranchWaypoint();
    if (!branch) {
      this.toastr.warning('Bitte zuerst eine Filiale mit Koordinaten wählen.', 'Tour optimieren');
      return;
    }
    if (mappable.length < 2) {
      this.toastr.warning('Mindestens 2 Aufträge mit Koordinaten nötig.', 'Tour optimieren');
      return;
    }

    const others = mappable;
    const waypoints = [branch, ...others.map(s => ({ lat: Number(s.lat), lng: Number(s.lng) }))];

    this.setTeamTourCalculating(team, true);
    this.orderService.getRouteDirections(waypoints, true).subscribe({
      next: res => {
        this.ngZone.run(() => {
          const order = Array.isArray(res.waypoint_order) ? res.waypoint_order : [];
          const optimizedMappable =
            order.length === others.length
              ? order.map((index: number) => others[index])
              : this.nearestNeighborOrder(mappable);
          this.applyOptimizedTeamStops(team, stops, optimizedMappable);
          this.setTeamTourCalculating(team, false);
          this.toastr.success(`Tour für Team ${team} optimiert.`, 'Tour optimieren');
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.applyOptimizedTeamStops(team, stops, this.nearestNeighborOrder(mappable));
          this.setTeamTourCalculating(team, false);
          this.toastr.warning(
            'Google-Optimierung fehlgeschlagen – Reihenfolge nach Entfernung gesetzt.',
            'Tour optimieren'
          );
          this.cdr.detectChanges();
        });
      }
    });
  }

  private getMappableTeamStops(team: DispatchTeamId): DispatchStop[] {
    return this.getTeamStops(team).filter(
      s => s.lat != null && s.lng != null && !s.geocodingFailed
    );
  }

  private applyOptimizedTeamStops(
    team: DispatchTeamId,
    allStops: DispatchStop[],
    optimizedMappable: DispatchStop[]
  ): void {
    const optimizedIds = new Set(optimizedMappable.map(s => s.id));
    const remainder = allStops.filter(s => !optimizedIds.has(s.id));
    this.setTeamStops(team, [...optimizedMappable, ...remainder]);
    this.reindexTeams();
    const plannerTeam = this.findTeam(team);
    if (plannerTeam) {
      plannerTeam.stops = [...plannerTeam.stops];
    }
    this.refreshMapMarkers(true);
    this.calculateTourForTeam(team, { drawOnMap: team === this.activeTourTeam });
  }

  private nearestNeighborOrder(stops: DispatchStop[]): DispatchStop[] {
    if (stops.length <= 1) {
      return [...stops];
    }

    const branch = this.getBranchWaypoint();
    const remaining = [...stops];
    const ordered: DispatchStop[] = [];
    let cLat = branch?.lat ?? Number(remaining[0].lat);
    let cLng = branch?.lng ?? Number(remaining[0].lng);

    while (remaining.length) {
      let bestIdx = 0;
      let bestDist = Infinity;

      remaining.forEach((stop, index) => {
        const dist = this.haversineMeters(cLat, cLng, Number(stop.lat), Number(stop.lng));
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = index;
        }
      });

      const next = remaining.splice(bestIdx, 1)[0];
      ordered.push(next);
      cLat = Number(next.lat);
      cLng = Number(next.lng);
    }

    return ordered;
  }

  getStopDistanceFromBranchKm(stop: DispatchStop): number | null {
    return getStopDistanceKmFromBranch(stop, this.getBranchWaypoint());
  }

  isStopOutsideBranchRadius(stop: DispatchStop): boolean {
    const distanceKm = this.getStopDistanceFromBranchKm(stop);
    return distanceKm != null && distanceKm > this.branchRadiusKm;
  }

  formatStopDistanceFromBranch(stop: DispatchStop): string {
    const distanceKm = this.getStopDistanceFromBranchKm(stop);
    if (distanceKm == null) {
      return '–';
    }
    return `${distanceKm.toLocaleString('de-DE', { maximumFractionDigits: 1 })} km`;
  }

  private haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const earthRadius = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  private calculateTourForTeam(
    team: DispatchTeamId,
    options: { showWarnings?: boolean; drawOnMap?: boolean } = {}
  ): void {
    const showWarnings = options.showWarnings ?? false;
    const drawOnMap = options.drawOnMap ?? team === this.activeTourTeam;
    const mappableStops = this.getMappableTeamStops(team);
    const waypoints = this.buildTeamRouteWaypoints(mappableStops);
    const minStops = this.getMinStopsForTour();

    if (mappableStops.length < minStops || waypoints.length < 2) {
      this.clearTeamTour(team);
      if (drawOnMap) {
        this.clearRoutePolyline();
      }
      this.setTeamTourCalculating(team, false);
      this.cdr.detectChanges();
      if (showWarnings) {
        if (this.selectedBranchId != null && !this.hasBranchWaypoint()) {
          this.toastr.warning('Filiale hat keine Koordinaten – Tour kann nicht berechnet werden.', 'Tour');
        } else {
          this.toastr.warning(
            this.hasBranchWaypoint()
              ? `Mindestens 1 Station in Team ${team} mit Koordinaten erforderlich.`
              : `Mindestens 2 Stationen in Team ${team} mit Koordinaten erforderlich.`,
            'Tour'
          );
        }
      }
      return;
    }

    this.setTeamTourCalculating(team, true);
    this.orderService.getRouteDirections(waypoints).subscribe({
      next: res => {
        this.ngZone.run(() => {
          const plannerTeam = this.findTeam(team);
          if (plannerTeam) {
            plannerTeam.tour = {
              distance: res.distance_text || '',
              duration: res.duration_text || '',
              legs: Array.isArray(res.legs) ? res.legs : [],
              path: Array.isArray(res.path) ? res.path : []
            };
            if (drawOnMap) {
              this.drawRoutePolyline(plannerTeam.tour.path, team);
            }
          }
          this.setTeamTourCalculating(team, false);
          this.cdr.detectChanges();
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.clearTeamTour(team);
          if (drawOnMap) {
            this.clearRoutePolyline();
          }
          this.setTeamTourCalculating(team, false);
          if (showWarnings) {
            this.toastr.error(`Tour für Team ${team} konnte nicht berechnet werden.`, 'Fehler');
          }
          this.cdr.detectChanges();
        });
      }
    });
  }

  private setTeamTourCalculating(team: DispatchTeamId, value: boolean): void {
    const plannerTeam = this.findTeam(team);
    if (plannerTeam) {
      plannerTeam.tourCalculating = value;
    }
    this.tourCalculating = this.teams.some(entry => entry.tourCalculating);
  }

  private recalculateToursForTeams(teams: DispatchTeamId[]): void {
    teams.forEach(team => {
      this.calculateTourForTeam(team, { drawOnMap: team === this.activeTourTeam });
    });
  }

  private teamsWithMappableStops(): DispatchTeamId[] {
    return this.teams
      .map(team => team.id)
      .filter(team => this.getMappableTeamStops(team).length >= this.getMinStopsForTour());
  }

  private collectAffectedTeams(sourceListId: string, targetListId: string): DispatchTeamId[] {
    const teams = new Set<DispatchTeamId>();
    const sourceTeam = this.resolveTeamIdFromListId(sourceListId);
    const targetTeam = this.resolveTeamIdFromListId(targetListId);
    if (sourceTeam) {
      teams.add(sourceTeam);
    }
    if (targetTeam) {
      teams.add(targetTeam);
    }
    return Array.from(teams);
  }

  private snapPlannerListLayout(): void {
    [0, 120, 300].forEach(delay => {
      window.setTimeout(() => this.clearPlannerItemTransforms(), delay);
    });
  }

  private clearPlannerItemTransforms(): void {
    if (document.querySelector('.dispatch-planner .cdk-drop-list-dragging')) {
      return;
    }

    document.querySelectorAll<HTMLElement>('.dispatch-planner .drop-list .roll-card-item').forEach(el => {
      if (
        el.classList.contains('cdk-drag-dragging') ||
        el.classList.contains('cdk-drag-placeholder') ||
        el.classList.contains('cdk-drag-animating')
      ) {
        return;
      }
      el.style.removeProperty('transform');
      el.style.removeProperty('position');
      el.style.removeProperty('top');
      el.style.removeProperty('left');
      el.style.removeProperty('width');
      el.style.removeProperty('height');
    });
  }

  generatePdf(): void {
    const stops = this.activeTeamStops;
    if (!stops.length) {
      this.toastr.warning(`Team ${this.activeTourTeam} ist leer.`, 'PDF');
      return;
    }

    const tour = this.findTeam(this.activeTourTeam)?.tour ?? EMPTY_TEAM_TOUR;
    const activeTeam = this.findTeam(this.activeTourTeam);
    this.pdfService.exportRollCard(stops, {
      distanceText: tour.distance,
      durationText: tour.duration,
      legs: tour.legs,
      routePath: tour.path,
      generatedAt: new Date(),
      branch: this.getBranchWaypoint(),
      teamNumber: this.activeTourTeam,
      teamLabel: this.getTeamBaseLabelForSlot(this.activeTourTeam) || activeTeam?.label || `Team ${this.activeTourTeam}`,
      tourDate: this.getPlanningDateLabel()
    }).then(() => {
      this.toastr.success(`${activeTeam?.label || `Team ${this.activeTourTeam}`} als PDF gespeichert.`, 'PDF');
    }).catch(() => {
      this.toastr.error('PDF konnte nicht erstellt werden.', 'PDF');
    });
  }

  openEmailDialog(): void {
    const stops = this.allTeamStops();
    if (!stops.length) {
      this.toastr.warning('Keine Aufträge in den Teamlisten.', 'E-Mail');
      return;
    }
    const distanceText = this.teams
      .filter(team => team.stops.length)
      .map(team => `${team.label}: ${team.tour.distance}`)
      .join(' · ');
    const durationText = this.teams
      .filter(team => team.stops.length)
      .map(team => `${team.label}: ${team.tour.duration}`)
      .join(' · ');

    this.dialog.open(DispatchPlannerEmailDialogComponent, {
      width: '680px',
      maxWidth: '95vw',
      data: {
        stops,
        distanceText,
        durationText
      }
    });
  }

  clearTourRoute(team?: DispatchTeamId): void {
    if (team) {
      this.clearTeamTour(team);
      if (team === this.activeTourTeam) {
        this.clearRoutePolyline();
      }
      return;
    }
    this.teams.forEach(entry => this.clearTeamTour(entry.id));
    this.clearRoutePolyline();
  }

  private clearTeamTour(team: DispatchTeamId): void {
    const plannerTeam = this.findTeam(team);
    if (plannerTeam) {
      plannerTeam.tour = { ...EMPTY_TEAM_TOUR };
    }
  }

  clearMapSelection(): void {
    this.selectedStopIds = [];
    this.selectedStopId = null;
    this.selectionRevision++;
    this.markerRefreshToken++;
    this.clearBranchToStopLine();
    this.refreshMapMarkers(false);
    this.cdr.detectChanges();
  }

  addSelectedToTeam(team: DispatchTeamId): void {
    if (!this.selectedAvailableCount) {
      return;
    }
    const selected = new Set(this.selectedStopIds);
    const toMove = this.availableStops.filter(s => selected.has(s.id));
    this.moveSelectedStopsToTeam(toMove, team, () => {
      this.availableStops = this.availableStops.filter(s => !selected.has(s.id));
    });
  }

  addSelectedDeliverySuggestionToTeam(team: DispatchTeamId): void {
    if (!this.selectedDeliverySuggestionCount) {
      return;
    }
    const selected = new Set(this.selectedStopIds);
    const toMove = this.deliverySuggestionStops.filter(s => selected.has(s.id));
    this.moveSelectedStopsToTeam(toMove, team, () => {
      this.deliverySuggestionStops = this.deliverySuggestionStops.filter(s => !selected.has(s.id));
    });
  }

  private moveSelectedStopsToTeam(
    toMove: DispatchStop[],
    team: DispatchTeamId,
    removeFromSource: () => void
  ): void {
    if (!toMove.length) {
      return;
    }
    const target = this.getTeamStops(team);
    this.setTeamStops(team, [...target, ...toMove]);
    removeFromSource();
    this.reindexTeams();
    this.clearMapSelection();
    this.refreshMapMarkers(true);
    this.recalculateToursForTeams([team]);
    toMove.forEach(stop => this.warnDuplicateInsert(stop, team));
    this.refreshDuplicateFlags();
  }

  private setTeamStops(team: DispatchTeamId, stops: DispatchStop[]): void {
    const plannerTeam = this.findTeam(team);
    if (plannerTeam) {
      plannerTeam.stops = stops;
    }
  }

  onOverlayMouseDown(event: MouseEvent): void {
    if (this.selectionTool === 'pan' || event.button !== 0) {
      return;
    }
    event.preventDefault();
    const point = this.getOverlayPoint(event);
    this.isDrawingSelection = true;
    this.selectionStart = point;
    this.selectionCurrent = point;
    this.selectionRect = null;
    this.lassoPoints = this.selectionTool === 'lasso' ? [point] : [];
    if (this.selectionTool === 'rectangle') {
      this.selectionRect = normalizeRect(point, point);
    }
  }

  onOverlayMouseMove(event: MouseEvent): void {
    if (!this.isDrawingSelection || !this.selectionStart) {
      return;
    }
    const point = this.getOverlayPoint(event);
    this.selectionCurrent = point;

    if (this.selectionTool === 'rectangle') {
      this.selectionRect = normalizeRect(this.selectionStart, point);
    } else {
      const last = this.lassoPoints[this.lassoPoints.length - 1];
      const dx = point.x - last.x;
      const dy = point.y - last.y;
      if (Math.hypot(dx, dy) >= 4) {
        this.lassoPoints = [...this.lassoPoints, point];
      }
    }
    this.cdr.markForCheck();
  }

  onOverlayMouseUp(event: MouseEvent): void {
    if (!this.isDrawingSelection) {
      return;
    }
    this.finishSelection(event);
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(event: MouseEvent): void {
    if (this.isDrawingSelection) {
      this.finishSelection(event);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.cancelDrawing();
    if (this.selectionTool !== 'pan') {
      this.setSelectionTool('pan');
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateMapSelectionOverlayLayout();
  }

  openImportDialog(): void {
    const ref = this.dialog.open(AddressImportDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {}
    });

    ref.afterClosed().subscribe((addresses: ParsedAddress[] | undefined) => {
      if (!addresses?.length) {
        return;
      }
      this.ngZone.run(() => this.addAddresses(addresses));
    });
  }

  openRollerSoapDialog(): void {
    const selectedBranch = this.branches.find((branch) => branch.id === this.selectedBranchId);
    const ref = this.dialog.open(RollerSoapImportDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        branchNo: selectedBranch?.short || '',
        branches: this.branches,
      }
    });

    ref.afterClosed().subscribe((result: RollerSoapImportDialogResult | undefined) => {
      if (!result) {
        return;
      }
      this.ngZone.run(() => this.importFromRollerSoap(result.branchNo, result.calendarDay));
    });
  }

  importFromRollerSoap(branchNo: string, calendarDay: string): void {
    this.rollerSoapLoading = true;
    this.cdr.detectChanges();

    this.rollerSoapService.fetchTouren(branchNo, calendarDay).pipe(
      finalize(() => {
        this.rollerSoapLoading = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (result) => {
        this.applyFilialeFromImport(result.stations);
        this.assignSoapStationsToTeams(result.stations);
        this.importPanelVisible = true;
        this.openSoapResultDialog(
          `${result.stations.length} Aufträge von Roller SOAP importiert.`,
          result.envelope,
          result.responseBody,
          false
        );
      },
      error: (err: DispatchPlannerSoapError | Error) => {
        const envelope = err instanceof DispatchPlannerSoapError ? err.envelope : '';
        const responseBody = err instanceof DispatchPlannerSoapError ? err.responseBody : '';
        this.openSoapResultDialog(
          err?.message || 'Roller SOAP Import fehlgeschlagen.',
          envelope,
          responseBody,
          true
        );
      }
    });
  }

  private openSoapResultDialog(
    message: string,
    envelope = '',
    responseBody = '',
    isError = true
  ): void {
    this.dialog.open(DispatchPlannerErrorDialogComponent, {
      width: '760px',
      maxWidth: '95vw',
      data: {
        title: isError ? 'Roller SOAP – Fehler' : 'Roller SOAP – Ergebnis',
        message,
        envelope: envelope || undefined,
        responseBody,
        isError,
      } as DispatchPlannerErrorDialogData
    });
  }

  openCsvPicker(): void {
    const input = this.csvInput?.nativeElement;
    if (!input) {
      this.toastr.error('Dateiauswahl nicht verfügbar.', 'CSV');
      return;
    }
    input.value = '';
    input.click();
  }

  openDeliverySuggestionPicker(): void {
    const input = this.deliverySuggestionInput?.nativeElement;
    if (!input) {
      this.toastr.error('Dateiauswahl nicht verfügbar.', 'Liefervorschlag');
      return;
    }
    input.value = '';
    input.click();
  }

  onDeliverySuggestionSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.ngZone.run(() => {
        try {
          const buffer = reader.result as ArrayBuffer;
          this.importDeliverySuggestionWorkbook(buffer);
        } catch {
          this.deliverySuggestionImportError = 'Liefervorschlagsliste konnte nicht gelesen werden.';
          this.toastr.error(this.deliverySuggestionImportError, 'Liefervorschlag');
        } finally {
          input.value = '';
          this.cdr.detectChanges();
        }
      });
    };
    reader.onerror = () => {
      this.ngZone.run(() => {
        this.deliverySuggestionImportError = 'Datei konnte nicht gelesen werden.';
        input.value = '';
        this.cdr.detectChanges();
      });
    };
    reader.readAsArrayBuffer(file);
  }

  importDeliverySuggestionWorkbook(buffer: ArrayBuffer): void {
    this.deliverySuggestionImportError = '';

    const rows = this.deliverySuggestionService.parseWorkbook(buffer);
    if (!rows.length) {
      this.deliverySuggestionImportError =
        'Keine Aufträge gefunden. Erwartete Spalte „Auftragsnummer“ in der Liefervorschlagsliste.';
      this.toastr.warning(this.deliverySuggestionImportError, 'Liefervorschlag');
      return;
    }

    this.deliverySuggestionPanelVisible = true;
    this.addFromDeliverySuggestions(rows);
    this.applyFilialeFromDeliverySuggestions(rows);
    this.toastr.success(`${rows.length} Liefervorschläge importiert.`, 'Liefervorschlag');
    this.cdr.detectChanges();
  }

  private applyFilialeFromDeliverySuggestions(rows: DeliverySuggestionRow[]): void {
    const codes = this.deliverySuggestionService.collectBranchCodes(rows);
    if (codes.length !== 1 || this.selectedBranchId != null) {
      return;
    }
    this.applyFilialeCode(codes[0], { showSuccessToast: true });
  }

  addFromDeliverySuggestions(rows: DeliverySuggestionRow[]): void {
    const newStops: DispatchStop[] = rows.map((row, idx) => {
      const addressParts = parsePlzOrtFromAddress(row.lieferadresse);
      const customer = resolveDeliverySuggestionCustomerFields(row);
      return {
      id: `${Date.now()}-lv-${idx}-${Math.random().toString(36).slice(2, 7)}`,
      address: row.lieferadresse,
      plz: addressParts.plz,
      ort: addressParts.ort,
      lat: null,
      lng: null,
      geocoding: true,
      geocodingFailed: false,
      orderIndex: 0,
      filiale: row.filiale,
      filialbezeichnung: row.filialbezeichnung,
      kaufvertragsnummer: row.kaufvertragsnummer,
      customerName: customer.customerName || undefined,
      nachname: customer.nachname || undefined,
      auftragsart: row.auftragsart,
      zahlungsart: row.zahlungsart,
      kundennummer: row.kundennummer,
      termin: row.termin,
      montagemenge: row.montagemenge,
      montageFlag: row.montage,
      lieferfaehigSeit: row.lieferfaehigSeit,
      zusatztext: row.zusatztext,
      rechnungsadresse: row.rechnungsadresse,
      telefon: row.telefon,
      mobil: row.mobil,
      kontingentLabel: this.buildDeliverySuggestionKontingentLabel(row),
      source: 'delivery-suggestion'
    };
    });
    this.geocodeStops(newStops, 'delivery-suggestion');
  }

  onCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.ngZone.run(() => {
        try {
          const buffer = reader.result as ArrayBuffer;
          this.importCsvContent(decodeCsvBytes(buffer));
        } catch {
          this.csvImportError = 'CSV konnte nicht gelesen werden.';
          this.toastr.error('CSV konnte nicht gelesen werden.', 'Import');
        } finally {
          input.value = '';
          this.cdr.detectChanges();
        }
      });
    };
    reader.onerror = () => {
      this.ngZone.run(() => {
        this.csvImportError = 'CSV-Datei konnte nicht gelesen werden.';
        input.value = '';
        this.cdr.detectChanges();
      });
    };
    reader.readAsArrayBuffer(file);
  }

  importCsvContent(content: string): void {
    this.csvImportError = '';

    const stations = this.csvService.parseAndGroup(content);
    if (!stations.length) {
      this.csvImportError =
        'Keine Kaufvertragsnummern gefunden. Erwartete Spalte „Kaufvertragsnummer“ (Trennzeichen ; oder ,).';
      this.toastr.warning(this.csvImportError, 'CSV');
      return;
    }

    this.addStationsFromCsv(stations);
    this.applyFilialeFromImport(stations, content);
    this.toastr.success(`${stations.length} Aufträge importiert.`, 'CSV');
    this.cdr.detectChanges();
  }

  onListDrop(event: CdkDragDrop<DispatchStop[]>): void {
    const sourceListId = event.previousContainer.id;
    const targetListId = event.container.id;

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    if (sourceListId !== targetListId) {
      const movedStop = event.container.data[event.currentIndex];
      if (movedStop) {
        const targetIsPool =
          targetListId === this.availableListId || targetListId === this.deliverySuggestionListId;
        if (targetIsPool) {
          movedStop.teamNumber = undefined;
          movedStop.orderIndex = 0;
        }
        if (targetListId === this.deliverySuggestionListId) {
          movedStop.source = 'delivery-suggestion';
        }
      }
    }

    const affectedTeams = this.collectAffectedTeams(sourceListId, targetListId);
    const targetTeam = this.resolveTeamIdFromListId(targetListId);

    this.reindexTeams();
    this.refreshPlannerListsAfterDrop(sourceListId, targetListId);
    if (targetTeam && sourceListId !== targetListId) {
      const movedStop = event.container.data[event.currentIndex];
      if (movedStop) {
        this.warnDuplicateInsert(movedStop, targetTeam);
      }
    }
    this.refreshDuplicateFlags();
    this.snapPlannerListLayout();
    this.refreshMapMarkers(true);
    this.recalculateToursForTeams(affectedTeams);
    this.cdr.detectChanges();
  }

  private refreshPlannerListsAfterDrop(sourceListId: string, targetListId: string): void {
    if (sourceListId === this.availableListId || targetListId === this.availableListId) {
      this.availableStops = [...this.availableStops];
    }
    if (sourceListId === this.deliverySuggestionListId || targetListId === this.deliverySuggestionListId) {
      this.deliverySuggestionStops = [...this.deliverySuggestionStops];
    }
    const affectedTeamIds = new Set<DispatchTeamId>();
    const sourceTeam = resolveTeamIdFromListId(sourceListId, this.teams);
    const targetTeam = resolveTeamIdFromListId(targetListId, this.teams);
    if (sourceTeam != null) {
      affectedTeamIds.add(sourceTeam);
    }
    if (targetTeam != null) {
      affectedTeamIds.add(targetTeam);
    }
    affectedTeamIds.forEach(teamId => {
      const team = this.findTeam(teamId);
      if (team) {
        team.stops = [...team.stops];
      }
    });
  }

  private resolveTeamIdFromListId(listId: string): DispatchTeamId | undefined {
    return resolveTeamIdFromListId(listId, this.teams);
  }

  moveStopToList(stop: DispatchStop, from: PlannerListKey, to: PlannerListKey, event?: Event): void {
    event?.stopPropagation();
    if (from === to) {
      return;
    }

    const sourceStops = this.getPlannerListStops(from).filter(s => s.id !== stop.id);
    this.setPlannerListStops(from, sourceStops);

    const targetStops = [...this.getPlannerListStops(to), stop];
    this.setPlannerListStops(to, targetStops);

    if (to === 'available' || to === 'delivery-suggestion') {
      stop.teamNumber = undefined;
      stop.orderIndex = 0;
    }

    const affectedTeams = this.collectAffectedTeams(
      this.plannerListKeyToId(from),
      this.plannerListKeyToId(to)
    );

    this.reindexTeams();
    this.snapPlannerListLayout();
    this.refreshMapMarkers(true);
    this.recalculateToursForTeams(affectedTeams);
    if (to !== 'available' && to !== 'delivery-suggestion') {
      this.warnDuplicateInsert(stop, to);
    }
    this.refreshDuplicateFlags();
  }

  private plannerListKeyToId(key: PlannerListKey): string {
    if (key === 'available') {
      return this.availableListId;
    }
    if (key === 'delivery-suggestion') {
      return this.deliverySuggestionListId;
    }
    return this.getTeamListId(key);
  }

  private getPlannerListStops(list: PlannerListKey): DispatchStop[] {
    if (list === 'available') {
      return this.availableStops;
    }
    if (list === 'delivery-suggestion') {
      return this.deliverySuggestionStops;
    }
    return this.getTeamStops(list);
  }

  private setPlannerListStops(list: PlannerListKey, stops: DispatchStop[]): void {
    if (list === 'available') {
      this.availableStops = stops;
      return;
    }
    if (list === 'delivery-suggestion') {
      this.deliverySuggestionStops = stops;
      return;
    }
    this.setTeamStops(list, stops);
  }

  addAddresses(addresses: ParsedAddress[]): void {
    const newStops: DispatchStop[] = addresses.map((addr, idx) => ({
      id: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 7)}`,
      address: addr.text,
      lat: null,
      lng: null,
      geocoding: true,
      geocodingFailed: false,
      orderIndex: 0,
      source: 'text'
    }));
    this.geocodeStops(newStops);
  }

  addStationsFromCsv(stations: DispatchCsvStation[]): void {
    const newStops = this.buildStopsFromCsvStations(stations);
    this.geocodeStops(newStops);
  }

  private buildStopsFromCsvStations(stations: DispatchCsvStation[]): DispatchStop[] {
    return stations.map((station, idx) => ({
      id: `${Date.now()}-csv-${idx}-${Math.random().toString(36).slice(2, 7)}`,
      address: station.address,
      lat: null,
      lng: null,
      geocoding: true,
      geocodingFailed: false,
      orderIndex: 0,
      filiale: station.filiale,
      kaufvertragsnummer: station.kaufvertragsnummer,
      kontingent: station.kontingent,
      kontingentLabel: station.kontingentLabel,
      customerName: station.customerName,
      nachname: station.nachname,
      plz: station.plz,
      ort: station.ort,
      termin: station.termin,
      tourBezeichnung: station.tourBezeichnung,
      tourSchluessel: station.tourSchluessel,
      positionCount: station.positionCount,
      artikelCount: station.artikelCount,
      liefermenge: station.liefermenge,
      lieferkostenEK: station.lieferkostenEK,
      montagemenge: station.montagemenge,
      montagekostenEK: station.montagekostenEK,
      gewichtKg: station.gewichtKg,
      volumenM3: station.volumenM3,
      kostenGesamt: station.kostenGesamt,
      articles: station.articles,
      email: station.email,
      mobil: station.mobil,
      telefon: station.telefon,
      source: 'csv' as const
    }));
  }

  private assignSoapStationsToTeams(stations: DispatchCsvStation[]): void {
    const newStops = this.buildStopsFromCsvStations(stations);
    if (!newStops.length) {
      return;
    }

    this.refreshTeamOptionsForBranch();
    let branchId = this.selectedBranchId;
    if (branchId == null) {
      const filialeHint = stations.map(station => station.filiale).find(code => !!code?.trim());
      if (filialeHint) {
        branchId = this.plannerTeamService.resolveBranchId(filialeHint, this.branches);
      }
    }

    const grouped = new Map<number | 'unassigned', DispatchStop[]>();

    newStops.forEach((stop) => {
      const teamBaseId = this.plannerTeamService.resolveTeamBaseIdForTourIdentifier(
        stop.tourSchluessel || '',
        branchId,
        this.allTeamBases
      );
      const key: number | 'unassigned' = teamBaseId ?? 'unassigned';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(stop);
    });

    let assignedCount = 0;
    const affectedTeamIds: DispatchTeamId[] = [];
    const stopsToGeocode: DispatchStop[] = [];

    grouped.forEach((stops, key) => {
      if (key === 'unassigned') {
        return;
      }

      const team = this.ensurePlannerTeamForTeamBase(key);
      const existingStops = this.getTeamStops(team.id);
      this.setTeamStops(team.id, [...existingStops, ...stops]);
      assignedCount += stops.length;
      affectedTeamIds.push(team.id);
      stopsToGeocode.push(...stops);
    });

    const unassigned = grouped.get('unassigned') || [];
    if (unassigned.length) {
      this.availableStops = [...this.availableStops, ...unassigned];
      stopsToGeocode.push(...unassigned);
    }

    if (stopsToGeocode.length) {
      this.geocodeExistingStops(stopsToGeocode);
    }

    this.reindexTeams();
    this.teams = [...this.teams];
    this.pruneEmptyPlannerTeamsAfterSoapImport(affectedTeamIds);

    if (affectedTeamIds.length) {
      this.activeDetailTeamId = affectedTeamIds[0];
      affectedTeamIds.forEach((teamId) => {
        const team = this.findTeam(teamId);
        if (team) {
          team.detailPanelVisible = true;
        }
      });
      if (this.activeTourTeam == null || !affectedTeamIds.includes(this.activeTourTeam)) {
        this.activeTourTeam = affectedTeamIds[0];
      }
    }

    this.refreshTeamCalendarAppointments();
    this.refreshMapMarkers(true);
    this.cdr.detectChanges();

    const teamCount = affectedTeamIds.length;
    if (assignedCount && teamCount) {
      this.toastr.success(
        `${assignedCount} Aufträge ${teamCount} Team${teamCount === 1 ? '' : 's'} zugeordnet` +
          (unassigned.length ? `, ${unassigned.length} ohne Tour-Zuordnung in Import.` : '.'),
        'Roller SOAP'
      );
    } else if (unassigned.length) {
      this.toastr.info(
        `${unassigned.length} Aufträge importiert – keine Tour-Zuordnung (KPD/TourSchluessel prüfen).`,
        'Roller SOAP'
      );
    }
  }

  private ensurePlannerTeamForTeamBase(teamBaseId: number): PlannerTeam {
    const existing = this.teams.find(team => Number(team.teamBaseId) === teamBaseId);
    if (existing) {
      return existing;
    }

    const team = createPlannerTeam(this.nextTeamId++);
    team.teamBaseId = teamBaseId;
    team.label = this.getTeamBaseLabelById(teamBaseId) || `Tour ${teamBaseId}`;
    team.detailPanelVisible = true;
    this.teams = [...this.teams, team];
    return team;
  }

  private pruneEmptyPlannerTeamsAfterSoapImport(_assignedTeamIds: DispatchTeamId[]): void {
    this.teams = this.teams.filter(
      team => team.stops.length > 0 || team.calendarAppointments.length > 0 || team.teamBaseId != null
    );

    if (!this.teams.length) {
      this.teams = createDefaultPlannerTeams();
    }

    if (!this.teams.some(team => team.id === this.activeDetailTeamId)) {
      this.activeDetailTeamId = this.teams[0]?.id ?? 1;
    }
    if (!this.teams.some(team => team.id === this.activeTourTeam)) {
      this.activeTourTeam = this.teams[0]?.id ?? 1;
    }
  }

  removeFromAvailable(stop: DispatchStop, event?: Event): void {
    event?.stopPropagation();
    this.availableStops = this.availableStops.filter(s => s.id !== stop.id);
    this.clearSelectionIfRemoved(stop.id);
    this.refreshMapMarkers(true);
  }

  removeFromTeam(stop: DispatchStop, team: DispatchTeamId, event?: Event): void {
    this.moveStopToList(stop, team, this.getStopHomeListKey(stop), event);
    this.clearSelectionIfRemoved(stop.id);
  }

  removeFromDeliverySuggestion(stop: DispatchStop, event?: Event): void {
    event?.stopPropagation();
    this.deliverySuggestionStops = this.deliverySuggestionStops.filter(s => s.id !== stop.id);
    this.clearSelectionIfRemoved(stop.id);
    this.refreshMapMarkers(true);
  }

  moveStopInTeam(team: DispatchTeamId, index: number, direction: -1 | 1, event?: Event): void {
    event?.stopPropagation();
    const stops = [...this.getTeamStops(team)];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= stops.length) {
      return;
    }
    moveItemInArray(stops, index, targetIndex);
    this.setTeamStops(team, stops);
    this.reindexTeams();
    const plannerTeam = this.findTeam(team);
    if (plannerTeam) {
      plannerTeam.stops = [...plannerTeam.stops];
    }
    this.snapPlannerListLayout();
    this.refreshMapMarkers(true);
    this.recalculateToursForTeams([team]);
    this.refreshDuplicateFlags();
    this.cdr.detectChanges();
  }

  addAllToTeam(team: DispatchTeamId): void {
    if (!this.availableStops.length) {
      return;
    }
    const toMove = [...this.availableStops];
    this.setTeamStops(team, [...this.getTeamStops(team), ...toMove]);
    this.availableStops = [];
    this.reindexTeams();
    this.refreshMapMarkers(true);
    this.recalculateToursForTeams([team]);
    toMove.forEach(stop => this.warnDuplicateInsert(stop, team));
    this.refreshDuplicateFlags();
    this.cdr.detectChanges();
  }

  clearTeam(team: DispatchTeamId): void {
    const stops = this.getTeamStops(team);
    if (!stops.length) {
      return;
    }
    stops.forEach(s => {
      s.teamNumber = undefined;
      s.orderIndex = 0;
    });
    this.availableStops = [...this.availableStops, ...stops];
    this.setTeamStops(team, []);
    this.reindexTeams();
    this.selectedStopId = null;
    this.refreshMapMarkers(true);
    this.recalculateToursForTeams([team]);
    this.refreshDuplicateFlags();
  }

  selectStop(stop: DispatchStop, event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      this.toggleMapSelection(stop.id);
      return;
    }
    this.focusStop(stop, { panMap: true });
  }

  /** Marker-Klick: Auftrag in der Liste hervorheben und dorthin scrollen. */
  focusStopFromMarker(stop: DispatchStop): void {
    this.focusStop(stop, { panMap: false, clearMultiSelection: true });
  }

  private focusStop(
    stop: DispatchStop,
    options: { panMap?: boolean; clearMultiSelection?: boolean } = {}
  ): void {
    if (options.clearMultiSelection) {
      this.selectedStopIds = [];
      this.selectionRevision++;
    }

    this.selectedStopId = stop.id;
    this.markerRefreshToken++;

    if (options.panMap && stop.lat != null && stop.lng != null && this.googleMap) {
      this.googleMap.panTo({ lat: Number(stop.lat), lng: Number(stop.lng) });
      this.googleMap.setZoom(14);
    }

    this.updateBranchToStopLine(stop);
    this.refreshMapMarkers(false);
    this.cdr.detectChanges();
    this.scrollToStopInList(stop.id);
  }

  private finishSelection(event: MouseEvent): void {
    if (!this.isDrawingSelection) {
      return;
    }
    this.isDrawingSelection = false;

    const endPoint = this.getOverlayPoint(event);
    const tool = this.selectionTool;
    const startPoint = this.selectionStart;
    const shiftKey = event.shiftKey;

    requestAnimationFrame(() => {
      this.ngZone.run(() => {
        this.updateMapSelectionOverlayLayout();

        if (!this.mapProjection.hasProjection()) {
          this.toastr.warning('Karte noch nicht bereit – bitte kurz warten und erneut versuchen.', 'Auswahl');
          this.cancelDrawing();
          this.cdr.detectChanges();
          return;
        }

        let selectedIds: string[] = [];

        if (tool === 'rectangle' && startPoint) {
          const rect = normalizeRect(startPoint, endPoint);
          if (rectMeetsMinimumSize(rect)) {
            selectedIds = this.findStopsInRect(rect);
          }
        } else if (tool === 'lasso') {
          const polygon = this.buildLassoPolygon(endPoint);
          if (polygon.length >= 3) {
            selectedIds = this.findStopsInPolygon(polygon);
          }
        }

        if (shiftKey && selectedIds.length) {
          this.applyMapSelection(selectedIds, true);
        } else {
          this.applyMapSelection(selectedIds, false);
        }

        if (selectedIds.length) {
          this.toastr.info(`${selectedIds.length} Auftrag${selectedIds.length === 1 ? '' : 'äge'} ausgewählt`, 'Kartenauswahl');
        }

        this.selectionStart = null;
        this.selectionCurrent = null;
        this.selectionRect = null;
        this.lassoPoints = [];
        this.scrollToFirstSelectedInList();
        this.cdr.detectChanges();
      });
    });
  }

  private applyMapSelection(ids: string[], append: boolean): void {
    const unique = append && ids.length
      ? Array.from(new Set([...this.selectedStopIds, ...ids]))
      : [...ids];

    this.selectedStopIds = unique;
    this.selectionRevision++;

    const firstSelected = unique.map(id => this.findStop(id)).find((stop): stop is DispatchStop => !!stop);
    this.selectedStopId = firstSelected?.id ?? null;

    this.markerRefreshToken++;
    this.refreshMapMarkers(false);
  }

  private scrollToFirstSelectedInList(): void {
    const stopId = this.selectedStopIds[0];
    if (!stopId) {
      return;
    }
    this.scrollToStopInList(stopId);
  }

  private scrollToStopInList(stopId: string): void {
    setTimeout(() => {
      const selector = `.roll-card-item[data-stop-id="${stopId}"]`;
      const el = document.querySelector(`.dispatch-planner ${selector}`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 50);
  }

  private buildLassoPolygon(endPoint: MapPoint): MapPoint[] {
    const points = [...this.lassoPoints];
    const last = points[points.length - 1];
    if (!last || last.x !== endPoint.x || last.y !== endPoint.y) {
      points.push(endPoint);
    }
    if (points.length >= 3) {
      const first = points[0];
      const closing = points[points.length - 1];
      if (first.x !== closing.x || first.y !== closing.y) {
        points.push({ ...first });
      }
    }
    return points;
  }

  /** Alle Aufträge mit Koordinaten auf der Karte (Import, Liefervorschlag, Team 1/2). */
  private selectableStopsForMapSelection(): DispatchStop[] {
    return this.mappedStops;
  }

  /**
   * Icon-Bounds in Map-DIV-Pixeln (gleiches Koordinatensystem wie die Google-Projektion).
   * Anchor liegt unten mittig am Lat/Lng-Punkt.
   */
  private markerIconBounds(stop: DispatchStop): MapRect | null {
    const anchor = this.mapProjection.latLngToPixel(Number(stop.lat), Number(stop.lng));
    if (!anchor) {
      return null;
    }
    return {
      x: anchor.x - this.markerIconAnchor.x,
      y: anchor.y - this.markerIconAnchor.y,
      width: this.markerIconSize.width,
      height: this.markerIconSize.height
    };
  }

  private rectsIntersect(a: MapRect, b: MapRect): boolean {
    return (
      a.x < b.x + b.width &&
      a.x + a.width > b.x &&
      a.y < b.y + b.height &&
      a.y + a.height > b.y
    );
  }

  private markerIntersectsPolygon(stop: DispatchStop, polygon: MapPoint[]): boolean {
    const bounds = this.markerIconBounds(stop);
    if (!bounds) {
      return false;
    }

    const samplePoints: MapPoint[] = [
      { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 },
      { x: bounds.x, y: bounds.y },
      { x: bounds.x + bounds.width, y: bounds.y },
      { x: bounds.x, y: bounds.y + bounds.height },
      { x: bounds.x + bounds.width, y: bounds.y + bounds.height }
    ];

    return samplePoints.some(point => pointInPolygon(point, polygon));
  }

  private findStopsInRect(rect: MapRect): string[] {
    return this.selectableStopsForMapSelection()
      .filter(stop => {
        const bounds = this.markerIconBounds(stop);
        if (!bounds) {
          return false;
        }
        return this.rectsIntersect(bounds, rect);
      })
      .map(stop => stop.id);
  }

  private findStopsInPolygon(polygon: MapPoint[]): string[] {
    if (polygon.length < 3) {
      return [];
    }

    return this.selectableStopsForMapSelection()
      .filter(stop => this.markerIntersectsPolygon(stop, polygon))
      .map(stop => stop.id);
  }

  private toggleMapSelection(id: string): void {
    const idx = this.selectedStopIds.indexOf(id);
    if (idx >= 0) {
      this.selectedStopIds = this.selectedStopIds.filter(x => x !== id);
    } else {
      this.selectedStopIds = [...this.selectedStopIds, id];
    }
    this.selectionRevision++;
    this.markerRefreshToken++;
    this.refreshMapMarkers(false);
    this.cdr.detectChanges();
  }

  private cancelDrawing(): void {
    this.isDrawingSelection = false;
    this.selectionStart = null;
    this.selectionCurrent = null;
    this.selectionRect = null;
    this.lassoPoints = [];
  }

  private getOverlayPoint(event: MouseEvent): MapPoint {
    const mapDiv = this.googleMap?.getDiv();
    if (!mapDiv) {
      return { x: 0, y: 0 };
    }
    const rect = mapDiv.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  private applyMapInteractionMode(): void {
    if (!this.googleMap) {
      return;
    }
    const selectionActive = this.selectionTool !== 'pan';
    this.googleMap.setOptions({
      draggable: !selectionActive,
      scrollwheel: !selectionActive,
      disableDoubleClickZoom: selectionActive,
      gestureHandling: selectionActive ? 'none' : 'auto'
    });
  }

  private geocodeStops(newStops: DispatchStop[], target: PlannerListKey = 'available'): void {
    if (!newStops.length) {
      return;
    }

    const targetStops = this.getPlannerListStops(target);
    this.setPlannerListStops(target, [...targetStops, ...newStops]);
    this.geocodeExistingStops(newStops);
  }

  private geocodeExistingStops(stops: DispatchStop[]): void {
    if (!stops.length) {
      return;
    }

    this.loadingGeocode = true;
    this.cdr.detectChanges();

    this.geocodeSubscription?.unsubscribe();
    this.geocodeSubscription = from(stops).pipe(
      mergeMap(
        stop =>
          this.geocodingService.geocodeAddress(stop.address).pipe(
            timeout(15000),
            map(loc => ({ stop, lat: loc.lat, lng: loc.lng })),
            catchError(() => of({ stop, lat: 0, lng: 0 }))
          ),
        2
      ),
      finalize(() => {
        this.ngZone.run(() => {
          this.loadingGeocode = false;
          this.markerRefreshToken++;
          this.refreshMapMarkers(true);
          this.refreshTeamCalendarAppointments();
          this.tryApplyFilialeFromImportedStops();
          this.recalculateToursForTeams(this.teamsWithMappableStops());
          this.cdr.detectChanges();
        });
      })
    ).subscribe({
      next: ({ stop, lat, lng }) => {
        this.ngZone.run(() => {
          stop.geocoding = false;
          if (lat && lng) {
            stop.lat = lat;
            stop.lng = lng;
          } else {
            stop.geocodingFailed = true;
          }
          this.markerRefreshToken++;
          this.refreshMapMarkers(false);
          if (stop.teamNumber && this.getMappableTeamStops(stop.teamNumber).length >= this.getMinStopsForTour()) {
            this.recalculateToursForTeams([stop.teamNumber]);
          }
          this.cdr.detectChanges();
        });
      }
    });
  }

  private findStop(id: string): DispatchStop | undefined {
    return (
      this.availableStops.find(s => s.id === id) ||
      this.deliverySuggestionStops.find(s => s.id === id) ||
      this.allTeamStops().find(s => s.id === id)
    );
  }

  private clearSelectionIfRemoved(id: string): void {
    if (this.selectedStopId === id) {
      this.selectedStopId = null;
      this.clearBranchToStopLine();
    }
    if (this.selectedStopIds.includes(id)) {
      this.selectedStopIds = this.selectedStopIds.filter(x => x !== id);
      this.selectionRevision++;
      this.markerRefreshToken++;
    }
  }

  private reindexTeams(): void {
    this.teams.forEach(team => this.reindexTeam(team.id));
  }

  private reindexTeam(team: DispatchTeamId): void {
    this.getTeamStops(team).forEach((stop, idx) => {
      stop.orderIndex = idx + 1;
      stop.teamNumber = team;
    });
  }

  private refreshMapMarkers(fitBounds: boolean): void {
    this.syncNativeMarkers();
    if (fitBounds) {
      this.fitMapToStops();
    }
  }

  private syncNativeMarkers(): void {
    if (!this.googleMap) {
      return;
    }

    this.clearNativeMarkers();
    this.syncBranchRadiusCircle();

    const branch = this.getBranchWaypoint();
    if (branch) {
      const branchIconUrl = buildBranchMarkerIcon();
      const branchMarker = new google.maps.Marker({
        map: this.googleMap,
        position: branch,
        title: this.selectedBranchLocation?.label || 'Filiale',
        zIndex: google.maps.Marker.MAX_ZINDEX + 1,
        optimized: false,
        icon: {
          url: branchIconUrl,
          scaledSize: new google.maps.Size(this.markerIconSize.width, this.markerIconSize.height),
          anchor: new google.maps.Point(this.markerIconAnchor.x, this.markerIconAnchor.y)
        }
      });
      this.nativeMarkers.push(branchMarker);
    }

    for (const stop of this.mappedStops) {
      const lat = Number(stop.lat);
      const lng = Number(stop.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        continue;
      }

      const iconUrl = this.getMarkerIconUrl(stop);
      const isSvg = iconUrl.startsWith('data:');
      const markerLabel = !isSvg ? this.getMarkerLabel(stop) : null;

      const marker = new google.maps.Marker({
        map: this.googleMap,
        position: { lat, lng },
        title: buildMarkerTooltipText(stop),
        optimized: false,
        zIndex: this.getMarkerZIndex(stop),
        icon: isSvg
          ? {
              url: iconUrl,
              scaledSize: new google.maps.Size(this.markerIconSize.width, this.markerIconSize.height),
              anchor: new google.maps.Point(this.markerIconAnchor.x, this.markerIconAnchor.y)
            }
          : { url: iconUrl },
        label: markerLabel
          ? { text: markerLabel, color: '#ffffff', fontWeight: '700' }
          : undefined
      });

      marker.addListener('click', () => {
        this.ngZone.run(() => this.focusStopFromMarker(stop));
      });

      this.nativeMarkers.push(marker);

      if (this.markerInfoLabelsVisible) {
        const html = buildMarkerInfoLabelHtml(stop);
        if (html) {
          const overlay = createMarkerInfoOverlay({ lat, lng }, html);
          overlay.setMap(this.googleMap);
          this.markerInfoOverlays.push(overlay);
        }
      }
    }

    for (const appointment of this.mappedCalendarAppointments) {
      const lat = Number(appointment.lat);
      const lng = Number(appointment.lng);
      const team = appointment.team ?? this.teams[0]?.id ?? 1;
      const teamIndex = Math.max(0, this.teams.findIndex(entry => entry.id === team));
      const iconUrl = buildCalendarAppointmentMarkerIcon(teamIndex);
      const marker = new google.maps.Marker({
        map: this.googleMap,
        position: { lat, lng },
        title: `${appointment.timeLabel} · ${appointment.title}`,
        zIndex: google.maps.Marker.MAX_ZINDEX,
        optimized: false,
        icon: {
          url: iconUrl,
          scaledSize: new google.maps.Size(this.markerIconSize.width, this.markerIconSize.height),
          anchor: new google.maps.Point(this.markerIconAnchor.x, this.markerIconAnchor.y)
        }
      });
      marker.addListener('click', () => {
        this.ngZone.run(() => this.focusCalendarAppointment(appointment));
      });
      this.nativeMarkers.push(marker);
    }

    google.maps.event.trigger(this.googleMap, 'resize');
  }

  private focusCalendarAppointment(appointment: DispatchCalendarAppointment): void {
    const team = appointment.team ?? this.teams[0]?.id ?? 1;
    this.selectTeamForDetail(team);
    const el = document.querySelector(
      `.dispatch-planner [data-calendar-id="${appointment.id}"]`
    );
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    this.cdr.detectChanges();
  }

  private clearNativeMarkers(): void {
    this.nativeMarkers.forEach(marker => marker.setMap(null));
    this.nativeMarkers = [];
    this.clearMarkerInfoOverlays();
    this.clearBranchRadiusCircle();
  }

  private clearMarkerInfoOverlays(): void {
    this.markerInfoOverlays.forEach(overlay => overlay.setMap(null));
    this.markerInfoOverlays = [];
  }

  private syncBranchRadiusCircle(): void {
    this.clearBranchRadiusCircle();
    if (!this.googleMap) {
      return;
    }

    const branch = this.getBranchWaypoint();
    if (!branch) {
      return;
    }

    this.branchRadiusCircle = new google.maps.Circle({
      map: this.googleMap,
      center: branch,
      radius: this.branchRadiusKm * 1000,
      strokeColor: '#1a5276',
      strokeOpacity: 0.7,
      strokeWeight: 2,
      fillColor: '#1a5276',
      fillOpacity: 0.08,
      clickable: false,
      zIndex: 1
    });

    this.branchInnerRadiusCircle = new google.maps.Circle({
      map: this.googleMap,
      center: branch,
      radius: this.branchInnerRadiusKm * 1000,
      strokeColor: '#1a5276',
      strokeOpacity: 0.95,
      strokeWeight: 2,
      fillColor: '#1a5276',
      fillOpacity: 0.14,
      clickable: false,
      zIndex: 2
    });
  }

  private clearBranchRadiusCircle(): void {
    if (this.branchInnerRadiusCircle) {
      this.branchInnerRadiusCircle.setMap(null);
      this.branchInnerRadiusCircle = null;
    }
    if (this.branchRadiusCircle) {
      this.branchRadiusCircle.setMap(null);
      this.branchRadiusCircle = null;
    }
  }

  private drawRoutePolyline(path: { lat: number; lng: number }[], team: DispatchTeamId = this.activeTourTeam): void {
    this.clearRoutePolyline();
    if (!this.googleMap || path.length < 2) {
      return;
    }
    const strokeColor = this.getTeamColor(team);
    this.routePolyline = new google.maps.Polyline({
      path: path.map(p => ({ lat: Number(p.lat), lng: Number(p.lng) })),
      geodesic: true,
      strokeColor,
      strokeOpacity: 0.85,
      strokeWeight: 4,
      map: this.googleMap
    });
  }

  private clearRoutePolyline(): void {
    if (this.routePolyline) {
      this.routePolyline.setMap(null);
      this.routePolyline = null;
    }
  }

  private updateBranchToStopLine(stop?: DispatchStop | null): void {
    this.clearBranchToStopLine();
    if (!this.googleMap) {
      return;
    }

    const branch = this.getBranchWaypoint();
    const target = stop ?? (this.selectedStopId ? this.findStop(this.selectedStopId) : null);
    if (!branch || !target?.lat || !target?.lng || target.geocodingFailed) {
      return;
    }

    this.branchStopPolyline = new google.maps.Polyline({
      path: [
        { lat: branch.lat, lng: branch.lng },
        { lat: Number(target.lat), lng: Number(target.lng) }
      ],
      geodesic: true,
      strokeColor: '#e74c3c',
      strokeOpacity: 0.9,
      strokeWeight: 3,
      zIndex: 5,
      map: this.googleMap
    });
  }

  private clearBranchToStopLine(): void {
    if (this.branchStopPolyline) {
      this.branchStopPolyline.setMap(null);
      this.branchStopPolyline = null;
    }
  }

  private fitMapToStops(): void {
    const points: { lat: number; lng: number }[] = [];
    const branch = this.getBranchWaypoint();
    if (branch) {
      points.push(branch);
    }
    this.mappedStops.forEach(stop => {
      const lat = Number(stop.lat);
      const lng = Number(stop.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        points.push({ lat, lng });
      }
    });
    this.mappedCalendarAppointments.forEach(appointment => {
      points.push({ lat: Number(appointment.lat), lng: Number(appointment.lng) });
    });

    if (points.length === 0) {
      if (this.googleMap) {
        this.googleMap.setCenter({ lat: 51.1657, lng: 10.4515 });
        this.googleMap.setZoom(6);
      } else {
        this.centerLat = 51.1657;
        this.centerLng = 10.4515;
        this.zoom = 6;
      }
      return;
    }

    if (this.googleMap) {
      const bounds = new google.maps.LatLngBounds();
      points.forEach(point => {
        bounds.extend(new google.maps.LatLng(point.lat, point.lng));
      });
      const radiusBounds = this.branchRadiusCircle?.getBounds();
      if (radiusBounds) {
        bounds.union(radiusBounds);
      }
      this.googleMap.fitBounds(bounds, 48);
      return;
    }

    if (points.length === 1) {
      this.centerLat = points[0].lat;
      this.centerLng = points[0].lng;
      this.zoom = 13;
      return;
    }

    const lats = points.map(p => p.lat);
    const lngs = points.map(p => p.lng);
    this.centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    this.centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
    this.zoom = 8;
  }
}
